/* landing-slider-gsap.js  —  Full-page landing slider on GSAP Observer
   Loaded deferred in <head> after gsap.min.js + Observer.min.js (template-index only).
   Custom slider (landing-slider.liquid) must be gated while this file is active.

   Exit strategy: outgoing slide is killed (tweens stopped, inline styles wiped,
   CSS animation classes removed) then instantly hidden. No exit choreography.
   Animation budget belongs entirely to the INCOMING card: beat 1 = card enters,
   beat 2 = text follows TEXT_STAGGER_DELAY later. Two beats only.

   Internally scrolling slides (Our Story, and the newsletter now that
   footer-group renders below it inside .page-content): the GSAP Observer stays
   enabled but is GUARDED — its onDown / onUp callbacks are no-ops while
   isScrollSlide(current). preventDefault is NOT set on the Observer (it is
   static and cannot be toggled). Instead a raw wheel listener fires first and
   calls e.preventDefault() for all non-scrolling slides (blocking native scroll
   there) and for boundary / momentum cases. This guarantees native scroll works
   on those slides with zero race conditions.  */

(function () {
  'use strict';

  if (typeof gsap === 'undefined' || typeof Observer === 'undefined') return;
  if (window.Shopify && window.Shopify.designMode) return;

  if (!document.body.classList.contains('page-index')) return;

  gsap.registerPlugin(Observer);

  /* ── Constants — tune here, never elsewhere ─────────────────────────────
     CARD_IN_DURATION  : how long the card/photo container takes to enter
     TEXT_STAGGER_DELAY: gap AFTER card lands before the text beat begins
     ADVANCE_COOLDOWN  : minimum dead window after timeline finishes; the
                         unlock is further deferred until wheel is quiet
     WHEEL_QUIET_MS    : additional ms of wheel silence required before the
                         lock releases after an advance; prevents momentum
                         tails from triggering a second advance globally
     UNLOCK_QUIET_MAX_MS: hard ceiling on how long scheduleUnlock may defer
                         itself waiting for WHEEL_QUIET_MS of wheel silence.
                         Trackpad inertia never goes quiet, so tryUnlock
                         rescheduled forever and the page stayed LOCKED for the
                         whole coast -- measured at 3.4 seconds past the
                         scheduled unlock on a single flick. Same failure as
                         GESTURE_HOLD_MAX_MS below, one layer down, and the two
                         compound: the gesture ceiling releases a call every
                         second into a lock the unlock guard is starving
     GESTURE_HOLD_MAX_MS: hard ceiling on how long the quiet window above may
                         keep extending itself. A TRACKPAD's inertial scrolling
                         emits wheel events for SECONDS after the fingers lift,
                         and each one refreshed the window, so input was held
                         until the inertia died -- measured at about five
                         seconds on desktop. A finger does not do this: touch
                         events stop when the finger does, which is why the
                         trailing refresh was right on mobile and wrong on a
                         trackpad
     GESTURE_QUIET_MS  : input silence required before advance() will act
                         again. Observer fires onUp/onDown REPEATEDLY through
                         one continuous drag -- measured at 9-12 calls per
                         swipe on a real iPhone -- so without this one gesture
                         is nine calls. NOT zeroed under reduced motion: this
                         is an input-handling guard, not an animation timing,
                         and zeroing it would hand reduced-motion users the
                         multi-advance bug
     BOTTOM_BUFFER     : subpixel tolerance for the osAtBottom() check
     SETTLE_QUIET_MS   : ms of wheel-event silence at a boundary before the
                         next fresh gesture is allowed to advance
     CONTENT_LEAD_OUT  : morph phase 0 — an OPTIONAL first out-stage, for a
                         slot that wants its content to leave in two beats
                         rather than one. Only runs if the outgoing slot
                         declares `contentLead`; slots without it are
                         unaffected and the chain is exactly as it was
     TRAIL_IN_DURATION : morph final beat — an OPTIONAL element that arrives
                         AFTER the incoming content has started. Reviews ->
                         newsletter uses it for the footer
     CONTENT_IN_STAGGER: gap between the incoming card's children as they
                         arrive. Was hardcoded at 0.05, which is below the
                         threshold anyone reads as sequence -- the newsletter's
                         two children (media, then copy) landed together and
                         Celeste reported the contents "plopping on".

                         MUST BE >= TEXT_IN_DURATION to read as a sequence, and
                         that is the whole trick. GSAP staggers the START of
                         each element, so at 0.18 against a 0.30 duration the
                         copy began while the media was still 60% faded in --
                         120ms of overlap on a 300ms fade, which the eye reads
                         as a cross-dissolve rather than one thing after
                         another. It was measurably staggered and still looked
                         simultaneous. At 0.30 the copy starts as the media
                         lands
     TRAIL_IN_OFFSET   : how far into the incoming content's fade the trail
                         starts. THE ONE DELIBERATE OVERLAP in this chain —
                         every other beat is chained on onComplete. Content and
                         footer arriving together reads as one event; strictly
                         sequencing them reads as two (Celeste, 2026-08-28)
     TEXT_OUT_DURATION : intro<->story morph, phase 1 — outgoing text fade
     MORPH_DURATION    : intro<->story morph, phase 2 — box rect tween
     TEXT_IN_DURATION  : intro<->story morph, phase 3 — incoming text fade
     CROSSFADE_OUT     : story<->product, product<->reviews — outgoing slide fade
     CROSSFADE_IN      : story<->product, product<->reviews — incoming slide fade
     TEXT_ITEM_DURATION: per-element fade in every slide's text beat
     TEXT_ITEM_STAGGER : gap between elements within that text beat
     HEADER_FADE       : nav hide/reveal either side of the intro slide
     SCRIM_FADE        : the glass behind the nav. LONGER than HEADER_FADE on
                         purpose — it is a large tinted mass, and at the nav's
                         own 0.3s it reads as a panel switching on rather than
                         haze gathering.
     VIDEO_REVEAL_DURATION : first load only — the eggshell curtain over the
                         background video fades off to reveal the water
     HERO_CARD_START   : when the intro card's mask begins, from timeline 0.
                         Deliberately mid-curtain so it lands in a partly
                         revealed scene rather than before anything happens
     HERO_CARD_DURATION: the wave fill's rise. SEPARATE from
                         CARD_IN_DURATION on purpose — that one is shared with
                         our-story, product-card and customer-reviews, so
                         retiming it here would silently retime all three
     HERO_CARD_FADE    : the card's opacity ramp, deliberately much shorter
                         than the mask. Running them together made the card
                         translucent for the whole wipe, so the eye read a
                         fade and the wipe was invisible. Short = opaque
                         almost immediately, then the mask IS the effect
     CASCADE_STEP      : the single interval between every item arriving on
                         the card — monogram, wordmark, rule, heading,
                         subheading, tagline. One value for all of them, on
                         purpose. It replaced LOGO_STAGGER, RULE_OVERLAP and
                         COPY_STAGGER plus two sequential holds, which between
                         them produced five different intervals and read as
                         arrhythmic. At 0.22s against 0.4-0.5s fades the items
                         overlap heavily, which is what makes it a cascade
                         rather than a queue
     LOGO_FADE         : monogram / wordmark fade, each -- currently the SAME
                         duration as TEXT_FADE (0.4s each). Corrected
                         2026-09-04: this previously read
                         "Longer than TEXT_FADE... the logo needs 0.5s where a
                         line of copy does not", which the code never matched
     RULE_WIPE         : the rule's scaleX wipe. A transform, not a fade, so
                         it keeps power2.out where the fades use power1.inOut
     TEXT_FADE         : heading / subheading / tagline, each. 0.4 is the
                         intended floor — below that the cascade stops
                         reading as one
     ARROW_GAP         : silence between the tagline landing and the arrow
                         starting, so the arrow reads as its own beat rather
                         than the end of the stagger. It's an affordance, not
                         part of the composition
     ARROW_DRAW        : the shaft's scaleY draw, top-down. The arrow is the
                         one element that doesn't just fade — it's the last
                         thing on screen and the only interactive affordance,
                         so it earns a gesture of its own
     ARROW_TIP_FADE    : the chevron tip, fading in as the shaft LANDS rather
                         than alongside it. Sequential, not concurrent: the
                         line draws to a point and the point then appears,
                         which reads as an arrow being drawn. Fading both
                         together would just be a slower version of the fade
                         this replaced
     SHIMMER_SWEEP     : the sheen's travel, left to right across the
                         tagline. Slow on purpose: a fast sweep reads as a
                         flicker rather than light moving over a surface.
                         Sets the end of the whole entrance
     CONTENT_OVERLAP   : how much the card's contents start BEFORE the mask
                         finishes. Negative = overlap. Part of the design, not
                         a fallback: contents-strictly-after pushes the whole
                         settle past 2.5s, and the lower content is uncovered
                         early by an upward mask anyway, so a small overlap
                         costs nothing visually and buys the time back */
  var CARD_IN_DURATION   = 0.35;  /* s  */
  var TEXT_STAGGER_DELAY = 0.1;   /* s  */
  var TEXT_ITEM_DURATION = 0.22;  /* s  */
  var TEXT_ITEM_STAGGER  = 0.05;  /* s  */
  var ADVANCE_COOLDOWN   = 0.1;   /* s  */
  var WHEEL_QUIET_MS     = 60;    /* ms */
  var GESTURE_QUIET_MS   = 300;   /* ms */
  var GESTURE_HOLD_MAX_MS = 1000; /* ms */
  var UNLOCK_QUIET_MAX_MS = 200;  /* ms */
  var BOTTOM_BUFFER      = 2;     /* px */
  var SETTLE_QUIET_MS    = 180;   /* ms */
  /* Ceiling on the morph's fonts.ready wait -- not a UX timer, stops an
     unresolved font promise from locking the page. */
  var FONTS_WAIT_MAX_MS  = 150;   /* ms */
  var CONTENT_LEAD_OUT   = 0.16;  /* s  */
  /* Was strictly sequential (0.45s dead time before the box moved); overlapping
     keeps both beats legible while cutting the out-phase to 0.28s. */
  var CONTENT_OUT_OVERLAP = 0.08;  /* s  */
  var TRAIL_IN_DURATION  = 0.25;  /* s  */
  var CONTENT_IN_STAGGER = 0.30;  /* s  */
  var TRAIL_IN_OFFSET    = 0.42;  /* s  */
  var TEXT_OUT_DURATION  = 0.20;  /* s  */
  /* MORPH_SAFETY_MS derives from this and MORPH_DURATION_SLOW, so neither can
     be retimed without the budget following. */
  var MORPH_DURATION     = 0.38;  /* s  */
  /* The box beat is TWO moves, not one, and that is Celeste's design call:
     the box grows from the reviews card's footprint to the newsletter card's
     SIZE while staying put, and only then travels to where the newsletter card
     actually sits. Animating position and size together (the original) read as
     one slide-and-stretch and lost the sense of the reviews box BECOMING the
     newsletter box.

     MORPH_SETTLE_DURATION is deliberately longer than MORPH_DURATION -- the
     grow is the transformation and wants confidence, the travel is the arrival
     and was asked for as "slowly into position". */
  var MORPH_SETTLE_DURATION = 0.55;  /* s  */
  /* Product Card -> Reviews moves two boxes and needs longer than the one-box
     newsletter grow: at MORPH_DURATION the pair landed as a snap. Staggering them
     was tried first and read as wrong -- the two boxes are visibly one unit, and
     splitting them looked like a glitch rather than a sequence. Same total length,
     spent on one slower simultaneous move instead. */
  var MORPH_DURATION_SLOW = 0.52;  /* s  */
  /* How long the synthetic box takes to fade IN over what it replaces, and the
     same window the replaced element fades OUT across. Only pairs whose source is
     not already eggshell need it: leaving Reviews the box lands on a card that is
     already the same colour, so it can appear instantly and nobody sees a seam.
     Leaving Our Story it lands on a PHOTOGRAPH, and appearing instantly is a hard
     cut. Fading the photo in the out-phase instead was worse -- the photo reached
     zero before the box existed, so the page video showed through in between. */
  var BOX_FADE_IN        = 0.22;  /* s  */
  /* The dots panel grows from its own centre rather than its contents fading
     in; the dots then appear together since staggering five read as fussy. */
  var GROW_IN_DURATION   = 0.30;  /* s  */
  var SHOW_AFTER_GROW    = 0.16;  /* s  */
  /* Reviews runs three items where the newsletter runs two, so the shared
     timings left its tail at 1.05s against 0.75s -- same beat, faster, for
     pairs that ask. Newsletter keeps the shared (slower) values. */
  var CONTENT_IN_OFFSET_FAST   = 0.08;  /* s  */
  var CONTENT_IN_DURATION_FAST = 0.22;  /* s  */
  var CONTENT_IN_STAGGER_FAST  = 0.12;  /* s  */
  /* Non-zero so the card's contents arrive while it's still moving, not after
     it parks -- the overlap Celeste asked for. */
  var CONTENT_IN_OFFSET  = 0.15;  /* s  */
  var TEXT_IN_DURATION   = 0.3;   /* s  */
  /* Our Story's arrival: FOUR elements, where the shared values give
     0.15 + 0.30x3 + 0.30 = 1.20s. The length is only half the problem -- a 0.30
     stagger against a 0.30 duration means each line FINISHES before the next
     starts, so nothing overlaps and four lines read as four arrivals rather than
     one movement. These give 0.04 + 0.07x3 + 0.18 = 0.43s AND put the stagger at
     0.39 of the duration, so the lines overlap into a cascade.

     THESE EXACT VALUES, not lower. They are what ran at 9dfbcf0, the last state
     confirmed working before the freeze; 6237522 tightened them to 0.02/0.15/0.05
     and that is the commit the stall first appeared in. Going faster than this
     means re-entering territory that has not been cleared. */
  var CONTENT_IN_OFFSET_OS   = 0.04;  /* s  */
  var CONTENT_IN_DURATION_OS = 0.18;  /* s  */
  var CONTENT_IN_STAGGER_OS  = 0.07;  /* s  */
  var CROSSFADE_OUT      = 0.3;   /* s  */
  var CROSSFADE_IN       = 0.35;  /* s  */
  var HEADER_FADE        = 0.3;   /* s  */
  var SCRIM_FADE         = 0.8;   /* s  */
  var VIDEO_REVEAL_DURATION = 1.0;   /* s  */
  var HERO_CARD_START       = 0.5;   /* s  */
  /* DO NOT SHORTEN THIS ALONE. CONTENT_OVERLAP below is an absolute number of
     seconds but only means anything RELATIVE to this value: at 2.1s the -1.30s
     overlap starts the cascade once the wave has cleared the content, at 1.4s
     the same -1.30s starts it at 0.6s and the logo and copy appear before the
     card is established. Tried on a draft 2026-09-15 as an LCP experiment and
     reverted on sight. Re-derive the overlap if this ever moves. */
  var HERO_CARD_DURATION    = 2.1;   /* s  */
  var HERO_CARD_FADE        = 0.15;  /* s  */
  var CONTENT_OVERLAP       = -1.30; /* s  */
  var CASCADE_STEP          = 0.18;  /* s  */
  var LOGO_FADE             = 0.4;   /* s  */
  var RULE_WIPE             = 0.4;   /* s  */
  var TEXT_FADE             = 0.4;   /* s  */
  var ARROW_GAP             = 0.10;  /* s  — was 0.24 */
  var ARROW_DRAW            = 0.26;  /* s  — was 0.32 */
  var ARROW_TIP_FADE        = 0.14;  /* s  — was 0.16 */
  var SHIMMER_OFFSET        = 0.15;  /* s  — after the tagline's slot opens */
  var SHIMMER_SWEEP         = 1.0;   /* s  — was 1.2 */

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (REDUCED_MOTION) {
    CARD_IN_DURATION   = 0;
    TEXT_STAGGER_DELAY = 0;
    TEXT_ITEM_DURATION = 0;
    TEXT_ITEM_STAGGER  = 0;
    ADVANCE_COOLDOWN   = 0;
    WHEEL_QUIET_MS     = 0;
    SETTLE_QUIET_MS    = 0;
    CONTENT_LEAD_OUT   = 0;
    CONTENT_OUT_OVERLAP = 0;
    CONTENT_IN_STAGGER = 0;
    CONTENT_IN_OFFSET_OS   = 0;
    CONTENT_IN_DURATION_OS = 0;
    CONTENT_IN_STAGGER_OS  = 0;
    TRAIL_IN_DURATION  = 0;
    TRAIL_IN_OFFSET    = 0;
    TEXT_OUT_DURATION  = 0;
    MORPH_DURATION     = 0;
    MORPH_SETTLE_DURATION = 0;
    MORPH_DURATION_SLOW = 0;
    BOX_FADE_IN        = 0;
    GROW_IN_DURATION   = 0;
    SHOW_AFTER_GROW    = 0;
    CONTENT_IN_OFFSET_FAST   = 0;
    CONTENT_IN_DURATION_FAST = 0;
    CONTENT_IN_STAGGER_FAST  = 0;
    CONTENT_IN_OFFSET  = 0;
    TEXT_IN_DURATION   = 0;
    CROSSFADE_OUT      = 0;
    CROSSFADE_IN        = 0;
    HEADER_FADE         = 0;
    SCRIM_FADE          = 0;
    VIDEO_REVEAL_DURATION = 0;
    HERO_CARD_START       = 0;
    HERO_CARD_DURATION    = 0;
    HERO_CARD_FADE        = 0;
    CONTENT_OVERLAP       = 0;
    CASCADE_STEP          = 0;
    LOGO_FADE             = 0;
    RULE_WIPE             = 0;
    TEXT_FADE             = 0;
    ARROW_GAP             = 0;
    ARROW_DRAW            = 0;
    ARROW_TIP_FADE        = 0;
    SHIMMER_OFFSET        = 0;
    SHIMMER_SWEEP         = 0;
  }

  /* Ceiling for the whole morph chain, DERIVED from the beats (not typed) so it
     can't drift when a duration changes -- not a UX timer, if this fires a
     tween was interrupted (see the safety net in doMorphTransition). Distinct
     from LOCK_CEILING_MS, the last-resort unlock for the whole slider, which
     must stay far above this. */
  var MORPH_SAFETY_MS = Math.round(
    (Math.max(CONTENT_LEAD_OUT, CONTENT_OUT_OVERLAP + TEXT_OUT_DURATION) +
     Math.max(MORPH_DURATION, MORPH_DURATION_SLOW) +
     Math.max(MORPH_SETTLE_DURATION,
              CONTENT_IN_OFFSET + TEXT_IN_DURATION + CONTENT_IN_STAGGER * 4,
              CONTENT_IN_OFFSET + TRAIL_IN_OFFSET + TRAIL_IN_DURATION)) * 1000
  ) + 400;

  /* Designer-specified entrance curve for the intro card: cubic-bezier(.22,
     .9, .24, 1). Implemented as a function, not a string, since GSAP can't
     parse cubic-bezier() natively without the CustomEase plugin, and only
     gsap.min.js/Observer.min.js are loaded here. Newton-Raphson solve for t
     given x, then evaluate y -- the same approach browsers use for CSS
     cubic-bezier; 8 iterations is well past convergence for curves in this
     range. */
  function cubicBezierEase(x1, y1, x2, y2) {
    function A(a, b) { return 1 - 3 * b + 3 * a; }
    function B(a, b) { return 3 * b - 6 * a; }
    function C(a)    { return 3 * a; }
    function calc(t, a, b)  { return ((A(a, b) * t + B(a, b)) * t + C(a)) * t; }
    function slope(t, a, b) { return 3 * A(a, b) * t * t + 2 * B(a, b) * t + C(a); }
    return function (x) {
      if (x <= 0) return 0;
      if (x >= 1) return 1;
      var t = x;
      for (var i = 0; i < 8; i++) {
        var d = calc(t, x1, x2) - x;
        var sl = slope(t, x1, x2);
        if (!sl) break;
        t -= d / sl;
      }
      return calc(t, y1, y2);
    };
  }
  var HERO_CARD_EASE = cubicBezierEase(0.22, 0.9, 0.24, 1);

  var container = document.querySelector('.page-content');
  if (!container) return;

  var slides = Array.from(
    container.querySelectorAll('.shopify-section.landing-section')
  );
  if (!slides.length) return;

  var OS_IDX = slides.findIndex(function (s) { return s.classList.contains('section-our-story'); });
  var PC_IDX = slides.findIndex(function (s) { return s.classList.contains('section-product-card'); });
  var CR_IDX = slides.findIndex(function (s) { return s.classList.contains('section-customer-reviews'); });
  var NL_IDX = slides.findIndex(function (s) { return s.classList.contains('section-flodesk-newsletter'); });

  /* Slides that scroll internally instead of advancing on gesture. Checks that
     ask "is this specifically Our Story" (enterOsAtBottomSettled, each
     timeline's own onComplete) stay on the raw index deliberately -- widening
     to this predicate is only correct for "does the CURRENT slide scroll".
     THE CARD MORPH USED TO BE OS-SPECIFIC ONLY; it now asks
     isScrollSlide(toIdx) since a second scrolling destination exists. */
  var SCROLL_IDXS = [OS_IDX, NL_IDX].filter(function (i) { return i !== -1; });
  function isScrollSlide(i) { return SCROLL_IDXS.indexOf(i) !== -1; }

  var current        = 0;
  var locked         = false;
  var timelines      = [];
  var _lastWheelTime = 0;    /* timestamp of the most recent wheel event */
  var _lastGestureAt = 0;    /* timestamp of the most recent advance() call, suppressed or not */
  var _lastAdvanceAt = 0;    /* timestamp of the most recent advance() call that was HONOURED */
  var _unlockTimer   = null; /* pending scheduleUnlock setTimeout handle */
  /* True only while SOME slide's entrance is running, any of them. Four places
     own its lifetime (goTo, enterViaEntranceTimeline, the hero's onComplete,
     skipEntrance) -- miss any one and it lies. */
  var _entranceSkippable = false;
  /* Whether the intro entrance has had its ONE run. Not a cached timeline --
     see buildHeroReturnTl for why caching one is impossible here. */
  var _heroEntrancePlayed = false;

  /* ── Lock watchdog ──────────────────────────────────────────────────────
     LAST-RESORT RECOVERY, not a UX timer. `locked` is set by advance() and by
     the init lock below, and the ONLY thing that clears it is scheduleUnlock
     firing off a timeline that completed. A timeline that never completes
     therefore strands the visitor on the current slide permanently, with no
     way to advance — the landing page's scroll IS this script.

     That is not hypothetical: a prefers-reduced-motion bug did exactly this
     on 2026-08-14 (see sections/ie-intro.liquid's hidden-state guard). That
     specific cause is fixed; this covers the class.

     ONE ceiling, armed by lock() and cleared by unlock(). A tighter one
     re-armed from scheduleUnlock() was tried and removed: tryUnlock()
     legitimately re-schedules itself for as long as wheel input keeps
     arriving (WHEEL_QUIET_MS), so a watchdog sized to delayMs fires during
     ordinary continuous scrolling and defeats the momentum-tail guard it
     sits next to. The failure this exists to catch is scheduleUnlock never
     being reached at all, which the single ceiling covers on its own.

     LOCK_CEILING_MS must stay comfortably ABOVE the longest legitimate lock,
     which is the first-load entrance — roughly 4-5s, and computed at runtime
     (arrowStart derives from tl.duration(), then SHIMMER_SWEEP follows it),
     so it cannot be asserted from the constants alone. 12s is far enough
     clear of that to never fire during normal playback. Do not "tighten" it
     toward the entrance length: firing early unlocks mid-entrance, which
     silently changes the skip behaviour rather than erroring. ── */
  var LOCK_CEILING_MS = 12000;
  var _lockWatchdog   = null;

  function armLockWatchdog(ms) {
    if (_lockWatchdog) clearTimeout(_lockWatchdog);
    _lockWatchdog = setTimeout(function () {
      _lockWatchdog = null;
      if (!locked) return;
      locked = false;
      if (window.console && console.warn) {
        console.warn('[landing-slider] lock watchdog fired after ' + ms +
          'ms — a timeline did not complete. Slider unlocked so the page ' +
          'stays navigable; the animation that stalled is the real bug.');
      }
    }, ms);
  }

  function lock() {
    locked = true;
    armLockWatchdog(LOCK_CEILING_MS);
  }

  function unlock() {
    locked = false;
    if (_lockWatchdog) {
      clearTimeout(_lockWatchdog);
      _lockWatchdog = null;
    }
  }

  /* Defers the actual unlock until both the minimum delay has passed AND the
     wheel has been quiet for WHEEL_QUIET_MS. Re-schedules itself if wheel is
     still active when the first delay fires. This prevents momentum tails from
     a hard flick triggering a second advance immediately after unlock. */
  function scheduleUnlock(delayMs) {
    if (_unlockTimer) clearTimeout(_unlockTimer);
    /* Absolute deadline, fixed at schedule time -- without it tryUnlock defers
       indefinitely, since trackpad inertia stamps _lastWheelTime continuously
       for seconds and the reschedule branch would run forever. Safe to cap
       now that GESTURE_QUIET_MS in advance() does the same momentum-tail job
       closer to the source; this trims the older, worse-placed half of a
       doubled-up guard. Inert under reduced motion (WHEEL_QUIET_MS is already
       zeroed, so the first condition is always true). */
    var deadline = Date.now() + delayMs + UNLOCK_QUIET_MAX_MS;
    function tryUnlock() {
      _unlockTimer = null;
      var age = Date.now() - _lastWheelTime;
      var capped = Date.now() >= deadline;
      if (age >= WHEEL_QUIET_MS || capped) {
        if (capped && age < WHEEL_QUIET_MS) {
        }
        unlock();
      } else {
        _unlockTimer = setTimeout(tryUnlock, WHEEL_QUIET_MS - age);
      }
    }
    _unlockTimer = setTimeout(tryUnlock, delayMs);
  }

  /* _osBottomSettleTimer/_osTopSettleTimer: one-shot timers that arm
     _osBottomSettled/_osTopSettled after SETTLE_QUIET_MS quiet at that edge.
     _osTouchY0: touchstart Y for Our Story swipe boundary detection. */
  var _osBottomSettleTimer = null;
  var _osTopSettleTimer    = null;
  var _osBottomSettled     = false;
  var _osTopSettled        = false;
  var _osTouchY0           = null;

  function osAtTop() { return container.scrollTop <= 1; }
  function osAtBottom() {
    var scrollable = container.scrollHeight - container.clientHeight;
    return scrollable <= 0 || container.scrollTop >= scrollable - BOTTOM_BUFFER;
  }

  function osScrollEnabled() {
    return document.body.classList.contains('ls-scrollable');
  }

  function osResetState() {
    clearTimeout(_osTopSettleTimer);
    clearTimeout(_osBottomSettleTimer);
    _osTopSettleTimer    = null;
    _osBottomSettleTimer = null;
    _osBottomSettled     = false;
    _osTopSettled        = false;
  }

  /* Called when Our Story becomes the active slide. */
  function osEnter() {
    container.scrollTop = 0;
  }

  /* Called when navigating away from Our Story (any direction). */
  function osExit() {
    osResetState();
    document.body.classList.remove('ls-scrollable');
    container.scrollTop = 0;   /* prevent carried-over scrollTop from clipping the next slide */
    _osTouchY0 = null;
  }

  function cleanAnimClasses(slide) {
    slide.querySelectorAll('.will-animate,.is-visible').forEach(function (el) {
      el.classList.remove('will-animate', 'is-visible');
    });
  }

  /* ── killSlide ──
     WARNING FOR ANYONE ADDING A TIMELINE LATER: this calls gsap.killTweensOf()
     on every element in the selector list below, and killTweensOf REMOVES
     tweens rather than pausing them. Any timeline targeting those elements is
     left EMPTY the first time its slide is exited -- and a GSAP timeline's
     duration is derived from its children, so it silently becomes a 0-duration
     timeline that completes instantly and renders nothing.

     The five original timelines survive because each is restarted from a build
     that still owns its tweens. Anything constructed AFTER init and held in a
     variable does not. That cost a full diagnosis cycle on 2026-08-28: the
     intro's return timeline was built once and cached, measured 0.57s in
     isolation, and reported 0.00s on the device -- because the isolated test
     never ran killSlide. It swapped correctly, ran correctly, and had nothing
     left to run, so the card returned showing only the two transform-hidden
     elements killSlide restores by hand.

     THE RULE: build such timelines on ENTRY, never cache them. Both entry paths
     need it -- goTo's own branch and enterViaEntranceTimeline -- and the
     product card already does exactly this for an unrelated reason. ── */
  function killSlide(slide) {
    var idx = slides.indexOf(slide);
    if (idx !== -1 && timelines[idx]) {
      timelines[idx].pause();
    }
    slide.querySelectorAll(
      '.ie-intro__card,.ie-intro__fill,.ie-intro__brand,.ie-intro__copy,.ie-intro__arrow,' +
      '.ie-intro__icon,.ie-intro__wordmark,.ie-intro__divider,' +
      '.ie-intro__heading,.ie-intro__subheading,.ie-intro__tagline,' +
      '.ie-intro__arrow-shaft,.ie-intro__arrow-tip,' +
      '.os__card,.os__photo-wrap,.os__heading,.os__divider,.os__lead,.os__body,' +
      '.pc__card,.pc__dots-panel,.pc__title-area,.pc__media-wrap,.pc__tagline,.pc__footer,' +
      '.cr__card,.cr__heading,.cr__review-title,.cr__quote-row,.cr__stars,.cr__author,.cr__footer,' +
      '.ns__card'
    ).forEach(function (el) {
      gsap.killTweensOf(el);
      el.classList.remove('will-animate', 'is-visible');
      /* Scoped to exactly what GSAP itself ever sets inline on these elements
         (opacity/transform-family) — NOT clearProps:'all'.

         CORRECTED 2026-08-28. This used to cite --pc-hole-cx/cy/r on .pc__card,
         "snapping the cutout mask to its CSS fallback (50%/389px/152px)". Those
         properties DO NOT EXIST -- not in product-card.liquid, not anywhere in
         the repo; the only occurrence left was this comment. The CSS mask that
         punched a hole through the card was replaced by .pc__circle, a
         self-contained looping video sitting on the card's own background
         (product-card.liquid:922), and the custom properties went with it.
         Behaviour is unchanged here; only the justification was wrong.

         The scoping is still correct, for a different and still-live reason:
         sections/product-card.liquid's paging script writes `transition` and
         `opacity` directly onto .pc__card while a card fades out
         (product-card.liquid:1500-1506, cleared on its own timer). Those share
         the inline style attribute, and clearProps:'all' wipes the attribute
         wholesale -- so it would strip the transition mid-fade and the outgoing
         card would cut rather than fade. Widening this list means checking that
         script first. */
      gsap.set(el, { clearProps: 'opacity,transform,translate,rotate,scale,transformOrigin,clipPath' });
    });

    /* The divider and arrow-shaft rest HIDDEN in CSS (scaleX/Y 0) for the
       first-load wave reveal, so clearProps above would strand them invisible
       forever on a return visit -- restored explicitly here instead, after
       the loop so they land after clearProps rather than being wiped by it.
       Same failure class as ie-intro.liquid's wave-fill "RESTING STATE IS
       COVERING" note, solved the other way since these can't express a safe
       CSS resting state. The arrow's tip needs no such fix -- it fades
       (resting opacity 0), which clearProps already handles normally. */
    slide.querySelectorAll('.ie-intro__divider').forEach(function (el) {
      gsap.set(el, { scaleX: 1 });
    });
    slide.querySelectorAll('.ie-intro__arrow-shaft').forEach(function (el) {
      gsap.set(el, { scaleY: 1 });
    });
  }

  /* ── Entrance timelines ── */

  function buildHeroTl() {
    if (!slides[0]) return null;
    var card  = slides[0].querySelector('.ie-intro__card');
    if (!card) return null;

    var icon     = card.querySelector('.ie-intro__icon');
    var wordmark = card.querySelector('.ie-intro__wordmark');

    /* Every one of these is optional in the schema except the rule, so each
       tween below is guarded rather than assumed. */
    var divider    = card.querySelector('.ie-intro__divider');
    var heading    = card.querySelector('.ie-intro__heading');
    var subheading = card.querySelector('.ie-intro__subheading');
    var tagline    = card.querySelector('.ie-intro__tagline');
    var arrowShaft = card.querySelector('.ie-intro__arrow-shaft');
    var arrowTip   = card.querySelector('.ie-intro__arrow-tip');

    /* Shadow carrier — see the .ie-intro rule in sections/ie-intro.liquid.
       The shadow lives on the section wrapper, not the card, because
       clip-path clips box-shadow too. It traces subtree alpha, so with the
       card background transparent it follows the wave's edge as it rises. */
    var wrap = slides[0].querySelector('.ie-intro');
    var fill = card.querySelector('.ie-intro__fill');

    /* onComplete reveals the nav. The entrance ends on the tagline shimmer
       (SHIMMER_SWEEP, the last tween added below), so this fires on the last
       beat of the intro card rather than on any one element finishing —
       nothing here needs updating if the tail of the sequence is re-ordered.

       It routes through syncHeader rather than tweening the header itself,
       so the header still has exactly one owner and the fade, the
       pointer-events flip and the _headerShown bookkeeping stay in one
       place. syncHeader(current), not syncHeader(0): if the entrance is
       still running when someone advances, the nav is already up and this
       must not contradict that. */
    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        /* BEFORE the NAV_REVEAL guard: the entrance has ended either way, and
           this flag must not outlive it. */
        _entranceSkippable = false;
        /* THE ENTRANCE HAS NOW RUN ITS ONCE. Swapping the slot rather than
           branching in goTo: goTo reads timelines[idx] and schedules the unlock
           off tl.duration(), so replacing the entry shortens the lock by itself
           and nothing downstream needs to know. Reached whether the entrance
           completed on its own or was skipped -- skipEntrance uses
           progress(1, false), which fires this. */
        _heroEntrancePlayed = true;
        if (NAV_REVEAL !== 'animation') return;
        _introRevealed = true;
        syncHeader(current);
      }
    });

    /* Opacity is its own short ramp, NOT paired with the mask. Running both
       over the full 900ms left the card translucent for the entire wipe, so
       all the eye registered was a fade — the mask was there but invisible
       underneath it. Getting to full opacity in ~150ms means the wipe plays
       out on a solid card, which is the point.

       y is cut deliberately (was 14 -> 0). clip-path insets are measured
       from the element's own border box, so translating the card while the
       mask runs slides the masked edge along with it and smears the wipe.
       The upward reveal already supplies the sense of rise. */
    tl.fromTo(card,
      { opacity: 0 },
      { opacity: 1, duration: HERO_CARD_FADE, ease: 'power1.out' },
      HERO_CARD_START
    );

    /* The wave IS the reveal. The card no longer carries a clip-path of its
       own — a straight-edged mask sweeping over the same 900ms fought the
       wave and read as two unrelated edges moving at once.

       This is a transform, so it composites rather than repainting: the whole
       reason for a rising fill over an animated mask-position. The clip-path
       on the fill is FIXED; only its position moves.

       Travels from fully below the card to covering it. The fill is taller
       than the card by more than the wave's depth, so at translateY(0) the
       solid body is past the top edge and nothing of the wave remains
       visible. */
    if (fill) {
      tl.fromTo(fill,
        { yPercent: 100 },
        { yPercent: 0, duration: HERO_CARD_DURATION, ease: HERO_CARD_EASE, force3D: true },
        HERO_CARD_START
      );
    }

    /* Shadow grows on the same curve and duration. Animated as custom
       properties on the wrapper rather than as a filter string, so GSAP
       interpolates plain numbers instead of re-parsing a function string
       each frame.

       NOTE: these are NEW values, not a match to anything existing. The card
       has never had a shadow, and there is no elevation convention to borrow
       — every box-shadow in this theme is an inset bevel on a button or
       input. Provisional pending designer sign-off; they're custom
       properties so changing them is a one-line edit in the CSS. */
    if (wrap) {
      tl.fromTo(wrap,
        { '--ie-card-shadow-blur': '0px', '--ie-card-shadow-alpha': 0 },
        {
          '--ie-card-shadow-blur': '28px', '--ie-card-shadow-alpha': 0.16,
          duration: HERO_CARD_DURATION, ease: HERO_CARD_EASE
        },
        HERO_CARD_START
      );
    }
    /* ── The card cascade ──
       Monogram, wordmark, rule, heading, subheading, tagline, arriving on ONE
       uniform interval. Every item hits CASCADE_STEP after the one before it,
       with no exceptions and no per-item reasoning.

       That uniformity is the whole point, and it replaces three constants and
       two sequential holds that had accreted one at a time — LOGO_STAGGER
       0.14, an 0.25 offset from RULE_OVERLAP, a 0.50 wait for the rule to
       finish, COPY_STAGGER 0.10, then a 0.40 wait for the subheading. Five
       different intervals, each defensible on its own, which together read as
       arrhythmic rather than as a cascade. A steady beat is the effect; the
       individual justifications were the problem.

       ITEMS OVERLAP THEIR PREDECESSORS AND THAT IS FINE. At 0.22s against
       fades of 0.4-0.5s, each item is roughly half-arrived when the next
       starts. The rule in particular is still wiping when the heading begins
       — deliberately, per the design call. Waiting for it was what made the
       gap before the copy so conspicuous.

       Timed off the wave's END rather than a fixed clock, so retuning
       HERO_CARD_DURATION carries the whole cascade along. See CONTENT_OVERLAP
       for why the start can sit this far before the wave formally finishes,
       and for the clearance derivation that floors it.

       SLOTS ARE POSITIONAL, held by index rather than by presence. The
       tagline is optional in the schema; if it is blank its slot still
       elapses, so an unset line cannot pull the rest of the cascade forward
       and change the rhythm of everything after it.

       EASE — power1.inOut on every fade, NOT power2.out. This is the other
       half of why the cascade read badly. power2.out is a positional ease:
       fast start, gentle settle, correct for something travelling. Applied to
       opacity it puts an element at 75% visible by the halfway point and then
       crawls, which reads as a pop followed by a lag rather than a fade.
       power1.inOut ramps gently at both ends and actually fades.

       The two TRANSFORMS keep power2.out, because for them it is the right
       ease: the rule's scaleX wipe and the arrow's scaleY draw are both
       movement, where a fast start settling gently is exactly what is wanted.
       Fade-in and travel want opposite curves; using one ease for both is
       what made this look uneven. */
    var cascadeStart = HERO_CARD_START + HERO_CARD_DURATION + CONTENT_OVERLAP;

    var cascade = [
      { el: icon,       dur: LOGO_FADE },
      { el: wordmark,   dur: LOGO_FADE },
      { el: divider,    dur: RULE_WIPE, wipe: true },
      { el: heading,    dur: TEXT_FADE },
      { el: subheading, dur: TEXT_FADE },
      { el: tagline,    dur: TEXT_FADE }
    ];

    for (var ci = 0; ci < cascade.length; ci++) {
      var item = cascade[ci];
      if (!item.el) continue;
      var at = cascadeStart + (ci * CASCADE_STEP);

      if (item.wipe) {
        /* scaleX, NOT opacity. The rule's resting opacity is 0.2 (its design
           value) and nothing here touches it — a growing line already reads
           as drawing itself, and fading at the same time muddies it.
           transformOrigin is set explicitly as well as in CSS so the wipe
           draws outward from the middle even if the inline transform is
           what's live at the time. */
        tl.fromTo(item.el,
          { scaleX: 0 },
          {
            scaleX: 1, duration: item.dur, ease: 'power2.out',
            transformOrigin: 'center center', force3D: true
          },
          at
        );
      } else {
        /* Fade only, no rise. The wave is the motion in this sequence and
           drifting content competes with it. */
        tl.fromTo(item.el,
          { opacity: 0 },
          { opacity: 1, duration: item.dur, ease: 'power1.inOut' },
          at
        );
      }
    }

    /* ARROW_GAP is measured from the tagline FINISHING, not starting, so the
       gap is genuinely empty rather than overlapping the copy's tail.
       ANCHORED TO tl.duration(), not an arithmetic copy of the copy's timing
       -- a hand-spelled anchor (ruleStart, then copyStart, then taglineStart)
       broke silently on the last two retimings, holding the arrow in place
       and swallowing the gap without erroring. tl.duration() follows any
       retiming of the rule/stagger/copy on its own. One behaviour change: an
       unset tagline now closes the gap instead of reserving its slot -- the
       better default, invisible today since the tagline is set. */
    var arrowStart = tl.duration() + ARROW_GAP;

    /* The arrow draws (shaft grows, then the chevron appears at its end)
       rather than fades -- two sequential tweens, the whole reason the tip is
       a real element and not ::after (GSAP can't target a pseudo-element).
       scaleY only, opacity untouched -- a line growing to length already
       reads as drawing itself, same as the rule's wipe. */
    if (arrowShaft) {
      tl.fromTo(arrowShaft,
        { scaleY: 0 },
        {
          scaleY: 1, duration: ARROW_DRAW, ease: 'power2.out',
          transformOrigin: 'top', force3D: true
        },
        arrowStart
      );
    }

    /* Tip starts where the shaft LANDS (arrowStart + ARROW_DRAW), not
       overlapping it -- computed from the constants rather than chained off
       the shaft tween, so an absent shaft can't silently shift its timing. */
    if (arrowTip) {
      tl.fromTo(arrowTip,
        { opacity: 0 },
        { opacity: 1, duration: ARROW_TIP_FADE, ease: 'power1.inOut' },
        arrowStart + ARROW_DRAW
      );
    }

    /* Tagline sheen — the last beat, and the only one that isn't part of
       getting the card on screen. Everything before this is arrival; this is
       the card settled and catching the light.

       Animates --ie-shimmer-pos, not a transform or opacity. The gradient and
       its background-clip live in the CSS (see .ie-intro__tagline); this only
       drives the position, so the colour decision stays in the stylesheet
       where it can be read next to the palette. Same custom-property approach
       as the card shadow above — GSAP interpolates the number and writes a
       concrete value each frame, so no @property registration is needed.

       100% -> 0% is LEFT TO RIGHT. Background-position percentages align the
       gradient's own edge to the element's, so with a gradient 2.5x the
       element's width, 100% parks the forest band off the left and 0% parks
       it off the right. Both ends hide it, which is what makes the resting
       state safe.

       linear ease, deliberately: light travelling across a surface moves at
       a constant rate. An eased sweep reads as the text animating rather
       than something moving over it.

       RUNS WITH THE TAGLINE, which is the thing it is a sheen ON. Two earlier
       anchors are worth knowing about, because both put it later than it
       belonged: tl.duration() + SHIMMER_GAP made it a separate closing
       gesture after the arrow tip landed, and arrowStart tied it to the arrow
       instead, which is a different element on the other side of ARROW_GAP.
       Anchored to the tagline's own slot it reads as light crossing that line
       while the line is still arriving.

       SHIMMER_OFFSET is measured from the slot OPENING, not from the fade
       finishing, so the sheen overlaps the tail of the tagline's own fade
       rather than queueing behind it.

       Positional, like every cascade slot: the tagline is the last entry, and
       the slot is computed from the array's length rather than from the
       element, so an unset tagline cannot move it. There is no shimmer to
       place in that case anyway — the tween below is guarded — but the value
       stays defined and the arithmetic stays readable.

       NOT anchored to tl.duration(): this block does not have to sit below
       the arrow's tweens, because it reads values computed above rather than
       the timeline's running length. */
    var taglineSlot  = cascadeStart + ((cascade.length - 1) * CASCADE_STEP);
    var shimmerStart = taglineSlot + SHIMMER_OFFSET;

    if (tagline) {
      tl.fromTo(tagline,
        { '--ie-shimmer-pos': '100%' },
        { '--ie-shimmer-pos': '0%', duration: SHIMMER_SWEEP, ease: 'none' },
        shimmerStart
      );
    }

    return tl;
  }

  /* The intro's RETURN timeline. The full entrance is a first impression and
     runs once; scrolling back up gets the same short arrival every other slide
     gets. Celeste's call, 2026-08-28, and it is better than the alternative in
     two ways: replaying a 3.35s wave sequence because someone scrolled up is a
     re-run nobody asked for, and it deletes the stuck-on-the-intro bug rather
     than working around it -- a ~0.5s lock has nothing worth skipping.

     IT MUST EXPLICITLY REVEAL SIX CHILDREN, and this is the part that would
     silently produce an empty card if missed. html.js-slider-ready is added
     before first paint and removed ONLY if the slider fails to boot, so on a
     healthy page it persists for the whole session -- and sections/ie-intro.liquid
     holds these at opacity 0 behind it:

       .ie-intro__icon .ie-intro__wordmark .ie-intro__heading
       .ie-intro__subheading .ie-intro__tagline .ie-intro__arrow-tip

     killSlide's clearProps strips GSAP's inline opacity on the way out, so they
     revert to that CSS 0. Only the full entrance ever set them back, which is
     precisely WHY the entrance replayed. Fading the card alone would bring back
     a solid card with nothing in it -- the same failure as the blank reviews
     card, one slide along.

     The divider and arrow-shaft need nothing here: their resting states are
     transform-hidden, and killSlide already restores them explicitly. Nor does
     the wave fill -- its resting state is COVERING, which is what gives the card
     its opaque background, so leaving it alone is correct.

     REBUILT ON EVERY ENTRY, NEVER CACHED, and that is not an optimisation
     choice. killSlide runs gsap.killTweensOf() over a selector list containing
     .ie-intro__card and all six of these children, and killTweensOf REMOVES
     tweens rather than pausing them. A cached instance is emptied the first
     time the visitor leaves the intro; an empty timeline has duration 0,
     completes instantly and renders nothing. See the rule at killSlide. */
  function buildHeroReturnTl() {
    if (!slides[0]) return null;
    var card = slides[0].querySelector('.ie-intro__card');
    if (!card) return null;

    var hiddenByCss = Array.prototype.slice.call(slides[0].querySelectorAll(
      '.ie-intro__icon,.ie-intro__wordmark,.ie-intro__heading,' +
      '.ie-intro__subheading,.ie-intro__tagline,.ie-intro__arrow-tip'
    ));

    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        _entranceSkippable = false;
      }
    });

    /* immediateRender: false on both, not optional -- a fromTo applies its
       FROM state at BUILD time by default, and this timeline is built inside
       the hero's onComplete, the instant the entrance finishes revealing
       everything. Without it, rendering opacity 0 there wiped the end state
       the entrance just produced (caught on the draft: all six children read
       0.00 immediately after the swap). Same trap goTo's own comment
       describes from the other direction, and the reason 9edd019 exists. */
    tl.fromTo(card,
      { opacity: 0, y: 14 },
      { opacity: 1, y: 0, duration: CARD_IN_DURATION, ease: 'power2.out',
        immediateRender: false },
      0
    );
    if (hiddenByCss.length) {
      tl.fromTo(hiddenByCss,
        { opacity: 0 },
        { opacity: 1, duration: TEXT_ITEM_DURATION, stagger: TEXT_ITEM_STAGGER,
          ease: 'power2.out', immediateRender: false },
        TEXT_STAGGER_DELAY
      );
    }
    return tl;
  }

  function buildOurStoryTl() {
    if (OS_IDX === -1 || !slides[OS_IDX]) return null;
    var slide   = slides[OS_IDX];
    var card    = slide.querySelector('.os__card');
    var photo   = slide.querySelector('.os__photo-wrap');
    if (!card) return null;

    var heading = card.querySelector('.os__heading');
    var divider = card.querySelector('.os__divider');
    var lead    = card.querySelector('.os__lead');
    var bodyEl  = card.querySelector('.os__body');

    var containers = [card, photo].filter(Boolean);
    var textItems  = [heading, divider, lead, bodyEl].filter(Boolean);

    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        /* Must not outlive the entrance -- without it, entranceRunning() keeps
           reporting true on a settled slide, and the next gesture "succeeds"
           at skipping a completed timeline, consuming the flag for nothing.
           Every entrance timeline needs this, not just the hero's. */
        _entranceSkippable = false;
        if (current !== OS_IDX) return;

        document.body.classList.add('ls-scrollable');
        osResetState();
        _osTopSettleTimer = setTimeout(function () {
          _osTopSettleTimer = null;
          if (current === OS_IDX && osAtTop()) {
            _osTopSettled = true;
          }
        }, SETTLE_QUIET_MS);
      }
    });

    tl.fromTo(containers,
      { opacity: 0, y: 32 },
      { opacity: 1, y: 0, duration: CARD_IN_DURATION, ease: 'power2.out', force3D: true }
    );

    if (textItems.length) {
      tl.fromTo(textItems,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: TEXT_ITEM_DURATION, stagger: TEXT_ITEM_STAGGER, ease: 'power2.out', force3D: true },
        '+=' + TEXT_STAGGER_DELAY
      );
    }

    return tl;
  }

  function buildProductCardTl() {
    if (PC_IDX === -1 || !slides[PC_IDX]) return null;
    var slide = slides[PC_IDX];

    /* Must target the ACTIVE .pc__card (one per product block, only one
       display:flex at a time) -- otherwise the entrance silently animates
       whichever card is first in the DOM regardless of which is visible. */
    var card      = slide.querySelector('.pc__card.pc__card--active') || slide.querySelector('.pc__card');
    var dotsPanel = slide.querySelector('.pc__dots-panel');
    if (!card) return null;

    var titleArea = card.querySelector('.pc__title-area');
    var mediaWrap = card.querySelector('.pc__media-wrap');
    var tagline   = card.querySelector('.pc__tagline');
    var footer    = card.querySelector('.pc__footer');

    var containers = [card, dotsPanel].filter(Boolean);
    var content    = [titleArea, mediaWrap, tagline, footer].filter(Boolean);

    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        /* Skip flag must not outlive the entrance -- same reasoning as
           buildOurStoryTl's onComplete above. */
        _entranceSkippable = false;
      }
    });

    /* scale (0.85->1) alongside opacity/y -- the real card visibly grows into
       size as it enters ("Option A"). Replaces an earlier box-morph ("Option
       B") that grew a separate blank placeholder into the card's rect before
       any content appeared -- approved, then reverted once seen live: it read
       as an extra empty "photo -> blank cream card -> product card" stage. */
    tl.fromTo(containers,
      { opacity: 0, y: 32, scale: 0.85 },
      { opacity: 1, y: 0, scale: 1, duration: CARD_IN_DURATION, ease: 'power2.out', force3D: true }
    );

    if (content.length) {
      tl.fromTo(content,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: TEXT_ITEM_DURATION, stagger: TEXT_ITEM_STAGGER, ease: 'power2.out', force3D: true },
        '+=' + TEXT_STAGGER_DELAY
      );
    }

    return tl;
  }

  function buildCustomerReviewsTl() {
    if (CR_IDX === -1 || !slides[CR_IDX]) return null;
    var slide = slides[CR_IDX];

    var cards = Array.from(slide.querySelectorAll('.cr__card'));
    if (!cards.length) return null;

    var heading   = slide.querySelector('.cr__heading');
    var rtitle    = slide.querySelector('.cr__review-title');
    var quoteRow  = slide.querySelector('.cr__quote-row');
    var viewToggle = slide.querySelector('.cr__view-toggle');
    var stars     = slide.querySelector('.cr__stars');
    var author    = slide.querySelector('.cr__author');
    var crFooter  = slide.querySelector('.cr__footer');

    var content = [heading, rtitle, quoteRow, viewToggle, stars, author, crFooter].filter(Boolean);

    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        /* Skip flag must not outlive the entrance -- same reasoning as
           buildOurStoryTl's onComplete above. */
        _entranceSkippable = false;
      }
    });

    tl.fromTo(cards,
      { opacity: 0, y: 32 },
      { opacity: 1, y: 0, duration: CARD_IN_DURATION, ease: 'power2.out', force3D: true }
    );

    if (content.length) {
      tl.fromTo(content,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: TEXT_ITEM_DURATION, stagger: TEXT_ITEM_STAGGER, ease: 'power2.out', force3D: true },
        '+=' + TEXT_STAGGER_DELAY
      );
    }

    return tl;
  }

  /* No content stagger here, unlike the other three build*Tl functions --
     Flodesk's embed injects its own DOM asynchronously via a remote script
     with unstable, randomized internal markup (see
     sections/flodesk-newsletter.liquid), so there's nothing addressable to
     stagger. The card container is the only thing this can animate. */
  function buildFlodeskNewsletterTl() {
    if (NL_IDX === -1 || !slides[NL_IDX]) return null;
    var card = slides[NL_IDX].querySelector('.ns__card');
    if (!card) return null;

    /* Mirrors buildOurStoryTl's onComplete: hand scrolling over to the browser
       once the entrance finishes, so footer-group (rendered inside
       .page-content on landing, see layout/theme.liquid) can be scrolled to.
       The current !== NL_IDX guard is NOT optional — onComplete can fire after
       a fast advance has already moved on, and without it this would enable
       container scrolling while a non-scrolling slide is active. */
    var tl = gsap.timeline({
      paused: true,
      onComplete: function () {
        /* Skip flag must not outlive the entrance -- same reasoning as
           buildOurStoryTl's onComplete above. */
        _entranceSkippable = false;
        if (current !== NL_IDX) return;
        document.body.classList.add('ls-scrollable');
        osResetState();
        _osTopSettleTimer = setTimeout(function () {
          _osTopSettleTimer = null;
          if (current === NL_IDX && osAtTop()) _osTopSettled = true;
        }, SETTLE_QUIET_MS);
      }
    });

    tl.fromTo(card,
      { opacity: 0, y: 32 },
      { opacity: 1, y: 0, duration: CARD_IN_DURATION, ease: 'power2.out', force3D: true }
    );

    return tl;
  }

  timelines[0] = buildHeroTl();
  if (OS_IDX !== -1) timelines[OS_IDX] = buildOurStoryTl();
  if (PC_IDX !== -1) timelines[PC_IDX] = buildProductCardTl();
  if (CR_IDX !== -1) timelines[CR_IDX] = buildCustomerReviewsTl();
  if (NL_IDX !== -1) timelines[NL_IDX] = buildFlodeskNewsletterTl();

  /* ── Intro ↔ Our Story MORPH transition ─────────────────────────────────
     Replaces the instant card-swap for this ONE pair only (0 <-> OS_IDX);
     every other pair keeps using goTo()'s existing instant-swap path below.

     No shared element exists between .ie-intro__card and .os__card (different
     sections, different sizes, both content-driven height/no CSS height) —
     so the "morph" is a synthetic cream box, NOT a clone of either card and
     NOT built with Flip.from/Flip.fit. A prior attempt used Flip to clone the
     card and left a stuck ghost on screen when cleanup didn't fire reliably;
     a single box this code creates and owns end-to-end is trivial to
     guarantee cleanup for, so that's the approach here. Flip.min.js was
     loaded for a while afterwards "in case", never registered and never
     called; it has since been removed — grep confirmed zero API calls and
     gsap.registerPlugin(Observer) as the only registration. If a future
     beats-model port wants it, re-add the script tag AND register it.

     Phases: TEXT OUT (outgoing text fades, card box stays put) -> BOX MORPH
     (synthetic box tweens rect + border-radius from the source card's
     measured rect to the destination card's measured rect) -> TEXT IN
     (incoming text fades in, then the existing scheduleUnlock/wheel-quiet
     system takes over exactly as it does for every other transition). ── */

  var MORPH_SLIDES = {};
  MORPH_SLIDES[0] = {
    idx: 0,
    section: slides[0],
    card: slides[0] && slides[0].querySelector('.ie-intro__card'),
    content: slides[0] ? Array.prototype.slice.call(
      /* Listed ALONGSIDE .ie-intro__copy, not instead of it -- these children
         carry their own opacity:0 hidden state, so fading only the container
         would land it at opacity 1 with all three still at 0 (an empty card).
         The arrow's TIP is listed but not its shaft: the tip is opacity-hidden
         (needs this fade), the shaft is scaleY-hidden (killSlide resets it
         explicitly instead) -- every opacity-hidden element belongs here,
         every transform-hidden one belongs in killSlide's reset. */
      slides[0].querySelectorAll(
        '.ie-intro__icon,.ie-intro__wordmark,.ie-intro__brand,.ie-intro__copy,' +
        '.ie-intro__heading,.ie-intro__subheading,.ie-intro__tagline,' +
        '.ie-intro__arrow,.ie-intro__arrow-tip'
      )
    ) : [],
  };
  if (OS_IDX !== -1 && slides[OS_IDX]) {
    MORPH_SLIDES[OS_IDX] = {
      idx: OS_IDX,
      section: slides[OS_IDX],
      card: slides[OS_IDX].querySelector('.os__card'),
      /* Leaving Our Story the visitor is always at the BOTTOM (every input path
         requires it), so what's on screen is the photograph plus the card too
         if the viewport never scrolled. The morph source is the visible union
         of the two (see sourceBoxesOf) -- a function since that union depends
         on scroll position, known only at morph time. */
      sourceUnion: function () {
        return [
          slides[OS_IDX].querySelector('.os__card'),
          slides[OS_IDX].querySelector('.os__photo-wrap')
        ].filter(Boolean);
      },
      /* The PHOTO IS NOT HERE -- it used to be, and drove to opacity 0 before
         startBoxMorph had created the box, flashing the landing video through
         where the photo had been. It cross-fades with the box instead (see
         crossFade); only the photo needs to hand over, the text still leaves
         in the out-phase. */
      content: Array.prototype.slice.call(
        slides[OS_IDX].querySelectorAll('.os__heading,.os__divider,.os__lead,.os__body')
      ),
      /* Fades OUT across exactly the window the box fades IN, so the photograph
         becomes eggshell rather than disappearing. DEPARTING only -- see
         contentTrail for the same element's arrival role. */
      crossFade: Array.prototype.slice.call(
        slides[OS_IDX].querySelectorAll('.os__photo-wrap')
      ),
      /* ARRIVING from the intro: fades in BELOW the card once its text lands,
         reusing the newsletter's footer-beat lane. Two roles for one element
         that cannot collide -- crossFade when Our Story is the SOURCE,
         contentTrail when it's the DESTINATION. */
      contentTrail: Array.prototype.slice.call(
        slides[OS_IDX].querySelectorAll('.os__photo-wrap')
      ),
    };
  }

  /* Reviews and Newsletter slots, for the box morph between them. content is
     the review card's OWN CHILDREN, not '.jdgm-widget' -- .cr__card--review
     has exactly one child, Shopify's own app-block wrapper (a contract we
     hold), where '.jdgm-widget' is Judge.me's and changes without notice. The
     difference is silent if missed: an empty content list skips the fade and
     the review text vanishes abruptly when the card is hidden -- hence the
     warn below. */
  if (CR_IDX !== -1 && slides[CR_IDX]) {
    MORPH_SLIDES[CR_IDX] = {
      idx: CR_IDX,
      section: slides[CR_IDX],
      card: slides[CR_IDX].querySelector('.cr__card--review'),
      /* Two boxes, primary first. Both are morph destinations arriving from
         Product Card (its card -> review box, its dots panel -> controls);
         leaving for Newsletter only the primary is used, since the pairing
         zips to the shorter list. */
      cards: Array.prototype.slice.call(
        slides[CR_IDX].querySelectorAll('.cr__card--review,.cr__card--controls')
      ),
      /* DEPARTING: flanking cards leave first, then the box's own contents;
         the box itself stays and morphs. */
      contentLead: Array.prototype.slice.call(
        slides[CR_IDX].querySelectorAll('.cr__card--title,.cr__card--controls')
      ),
      content: (function () {
        var box = slides[CR_IDX].querySelector('.cr__card--review');
        return box ? Array.prototype.slice.call(box.children) : [];
      })(),
      /* ARRIVING: header card, then reviews, then carousel, per Celeste --
         the header is here rather than in `cards` since nothing morphs into
         it, it simply appears. */
      contentIn: (function () {
        var out = [];
        var title = slides[CR_IDX].querySelector('.cr__card--title');
        var box   = slides[CR_IDX].querySelector('.cr__card--review');
        var car   = slides[CR_IDX].querySelector('.cr__carousel');
        if (title) out.push(title);
        if (box) out = out.concat(Array.prototype.slice.call(box.children));
        if (car) out.push(car);
        return out;
      })(),
    };
  }
  /* FUNCTIONS, not arrays: .pc__card--active moves when the visitor pages the
     carousel, so anything captured at init would morph the wrong card. See
     resolveSlot. */
  if (PC_IDX !== -1 && slides[PC_IDX]) {
    MORPH_SLIDES[PC_IDX] = {
      idx: PC_IDX,
      section: slides[PC_IDX],
      cards: function () {
        return [
          slides[PC_IDX].querySelector('.pc__card--active'),
          slides[PC_IDX].querySelector('.pc__dots-panel')
        ].filter(Boolean);
      },
      /* Beat 1: everything inside both boxes goes at once, leaving two plain
         eggshell rectangles for beat 2 to move. .pc__card-inner as ONE
         element rather than its children so the circle goes with the rest
         without naming it. Dots go too -- beat 2 replaces the panel with a
         blank box, and dots still showing then would pop out rather than
         fade. */
      content: function () {
        var active = slides[PC_IDX].querySelector('.pc__card--active');
        var panel  = slides[PC_IDX].querySelector('.pc__dots-panel');
        var out = [];
        var inner = active && active.querySelector('.pc__card-inner');
        if (inner) out.push(inner);
        if (panel) out = out.concat(Array.prototype.slice.call(panel.children));
        return out;
      },
      /* ARRIVING: only the card's body fades. The panel is not here -- it grows,
         see growIn -- and its dots are not here either, or they would fade in
         while the panel was still part-way through scaling and arrive squashed. */
      contentIn: function () {
        var active = slides[PC_IDX].querySelector('.pc__card--active');
        var inner  = active && active.querySelector('.pc__card-inner');
        return inner ? [inner] : [];
      },
      /* Grows out from its own centre, under the card. scaleX, so the panel opens
         to both sides at once rather than unrolling from one edge. */
      growIn: function () {
        var panel = slides[PC_IDX].querySelector('.pc__dots-panel');
        return panel ? [panel] : [];
      },
      /* Revealed together the moment the panel has finished growing. */
      showAfterGrow: function () {
        var panel = slides[PC_IDX].querySelector('.pc__dots-panel');
        return panel ? Array.prototype.slice.call(panel.children) : [];
      }
    };
  }
  if (NL_IDX !== -1 && slides[NL_IDX]) {
    MORPH_SLIDES[NL_IDX] = {
      idx: NL_IDX,
      section: slides[NL_IDX],
      card: slides[NL_IDX].querySelector('.ns__card'),
      /* Beat 4. */
      content: (function () {
        var card = slides[NL_IDX].querySelector('.ns__card');
        return card ? Array.prototype.slice.call(card.children) : [];
      })(),
      /* Beat 5. Footer group is a SIBLING of <main>, not inside the slide, so
         it's queried from the document -- 93px of it sits above the fold at
         393x702 (the designed peek), but this beat runs regardless of whether
         the peek is on screen. */
      contentTrail: Array.prototype.slice.call(
        document.querySelectorAll('.shopify-section-group-footer-group')
      ),
    };
  }

  (function warnEmptyMorphSlots() {
    [CR_IDX, NL_IDX].forEach(function (i) {
      var slot = MORPH_SLIDES[i];
      if (!slot) return;
      if (!slot.card && window.console && console.warn) {
        console.warn('[landing-slider] morph slot ' + i + ' has no card; the ' +
          'morph will fall back to a plain swap.');
      }
      if (slot.card && !slot.content.length && window.console && console.warn) {
        console.warn('[landing-slider] morph slot ' + i + ' resolved ZERO content ' +
          'elements. The fade will be skipped and the card will vanish abruptly. ' +
          'Most likely the markup under it changed.');
      }
    });
  })();

  /* MORPH DISABLED 2026-08-27 -- temporary, pending the interrupt fix.
     doMorphTransition chains four GSAP tweens by onComplete with the only
     scheduleUnlock in the last one, so a killed tween strands the slider
     (current unadvanced, nothing scheduled to unlock, recoverable only by the
     12s watchdog). Disabled at the DISPATCH so goTo falls through to its
     ordinary killSlide/swap/restart/scheduleUnlock path -- the same one
     reduced-motion users already take. TO RESTORE: set true once the chain
     handles interruption (onInterrupt per tween, plus a safety that ARRIVES
     rather than merely unlocking) -- half built in
     scratchpad/wip-flag-and-safety-net.patch. */
  var MORPH_ENABLED = false;

  /* SEPARATE FROM MORPH_ENABLED -- that flag is false because intro <-> Our
     Story strands the slider specifically; re-enabling it to get reviews ->
     newsletter would switch a known-broken pair back on for no reason.
     FORWARD ONLY by design: contentLead/contentTrail are ROLES, not slots, so
     the reverse needs its own choreography. Going back takes the directional
     crossfade below instead. */
  /* `travel`: grow around the source's own centre first, then move to the
     destination -- only right when the destination is roughly concentric.
     Newsletter: yes (grows then lifts). Product Card -> Reviews: no -- the
     destination is smaller AND lower, so growing first drives the bottom edge
     UP before the travel brings it back down; moving+resizing together is
     monotonic instead. */
  var BOX_MORPH_FORWARD = [];
  /* Intro -> Our Story is `177f844` reapplied and nothing else -- blamed for
     a freeze and switched off twice, but both times the comparison baseline
     was contaminated by an incompletely-reverted commit; the pair itself was
     never shown to be at fault (Celeste caught this). NO TIMING OVERRIDES
     deliberately -- those were added AFTER this pair worked and are what
     actually correlated with the stall. */
  if (OS_IDX !== -1 && MORPH_SLIDES[0]) BOX_MORPH_FORWARD.push({
    from: 0, to: OS_IDX, travel: true,
    contentOffset:   CONTENT_IN_OFFSET_OS,
    contentDuration: CONTENT_IN_DURATION_OS,
    contentStagger:  CONTENT_IN_STAGGER_OS
  });
  if (OS_IDX !== -1 && PC_IDX !== -1) BOX_MORPH_FORWARD.push({
    /* MORPH_DURATION, not _SLOW. The slow value exists for Product Card ->
       Reviews, where TWO boxes move and a shorter beat read as a snap. This pair
       moves ONE box, and at 0.52 it made leaving Our Story the slowest transition
       on the page -- 0.20 out + 0.52 box + 0.55 settle + 0.10 cooldown = ~1.37s of
       held input, which is what "slow on Chrome mobile" is. 0.38 takes it to
       ~1.23s. Reusing the existing constant rather than adding another: every
       ordering mistake tonight came from new declarations. */
    from: OS_IDX, to: PC_IDX, travel: false, duration: MORPH_DURATION,
    boxFadeIn: BOX_FADE_IN
  });
  if (PC_IDX !== -1 && CR_IDX !== -1) BOX_MORPH_FORWARD.push({
    from: PC_IDX, to: CR_IDX, travel: false, duration: MORPH_DURATION_SLOW,
    contentOffset:   CONTENT_IN_OFFSET_FAST,
    contentDuration: CONTENT_IN_DURATION_FAST,
    contentStagger:  CONTENT_IN_STAGGER_FAST
  });
  if (CR_IDX !== -1 && NL_IDX !== -1) BOX_MORPH_FORWARD.push({ from: CR_IDX, to: NL_IDX, travel: true });

  function morphOptionsFor(a, b) {
    for (var i = 0; i < BOX_MORPH_FORWARD.length; i++) {
      if (BOX_MORPH_FORWARD[i].from === a && BOX_MORPH_FORWARD[i].to === b) return BOX_MORPH_FORWARD[i];
    }
    return null;
  }

  /* No `.card` check here -- Product Card resolves its boxes lazily, so the
     property doesn't exist until resolveSlot runs. doMorphTransition's own
     null-guard still falls back to a plain swap if a card turns up missing. */
  function isBoxMorphForward(a, b) {
    return !!MORPH_SLIDES[a] && !!MORPH_SLIDES[b] && !!morphOptionsFor(a, b);
  }

  function isMorphPair(a, b) {
    return OS_IDX !== -1 && ((a === 0 && b === OS_IDX) || (a === OS_IDX && b === 0));
  }

  /* A morph can drive MORE THAN ONE box (Product Card -> Reviews moves two at
     once). These are parallel arrays only in that both are swept together --
     nothing indexes one by the other. */
  var _morphBoxes      = []; /* synthetic eggshell boxes in flight */
  var _morphTween      = null; /* whichever tween/timeline is driving the morph */
  var _morphHiddenEls  = []; /* real elements hidden mid-morph, for failsafe restore */

  function rectOf(el) {
    var r = el.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }

  /* Both SCROLL SLIDES, not Our Story only -- renamed from withOsActiveLayout
     when the newsletter got a morph slot. Inactive they are
     position:absolute/height:100%/align-items:center; active it's
     position:relative/height:auto/align-items:flex-start (normal flow, so
     .page-content can scroll it) -- a real layout difference, not just
     visibility (assets/critical.css, `.shopify-section.landing-section.ls-active`).
     This simulates that exact rule via inline styles, synchronously, so the
     morph never snaps once .ls-active applies for real.
     THE CLASS IS PART OF THE SIMULATION, not just the properties below --
     copying the active rule by hand reproduces what HANGS OFF the class
     elsewhere too, e.g. the footer reveal selector
     (`.section-flodesk-newsletter.ls-active ~ .shopify-section-group-footer-group`,
     assets/critical.css) that the newsletter's own --ns-offset depends on;
     without the class the footer measures 0 and the offset is wrong by
     exactly the footer's height (measured 146px at 1474x772). __lsRelayout is
     the synchronous recompute -- the newsletter's own MutationObserver can't
     run inside a measure-and-revert that never yields. */
  function withActiveLayout(section, fn) {
    var prev = {
      position: section.style.position,
      height: section.style.height,
      top: section.style.top,
      left: section.style.left,
      right: section.style.right,
      alignItems: section.style.alignItems
    };
    var hadActive = section.classList.contains('ls-active');
    section.classList.add('ls-active');
    section.style.position   = 'relative';
    section.style.height     = 'auto';
    section.style.top        = 'auto';
    section.style.left       = 'auto';
    section.style.right      = 'auto';
    section.style.alignItems = 'flex-start';
    if (typeof section.__lsRelayout === 'function') section.__lsRelayout();
    var result = fn();
    section.style.position   = prev.position;
    section.style.height     = prev.height;
    section.style.top        = prev.top;
    section.style.left       = prev.left;
    section.style.right      = prev.right;
    section.style.alignItems = prev.alignItems;
    if (!hadActive) section.classList.remove('ls-active');
    /* Restore the offset to match the state we are handing back, or the card
       keeps an offset derived from a footer that is display:none again. */
    if (typeof section.__lsRelayout === 'function') section.__lsRelayout();
    return result;
  }

  /* WHICH SLIDES NEED THE SIMULATION IS DERIVED, NOT DECLARED. It used to read
     slot.isOurStory, a hand-set boolean, and that was wrong the moment a second
     scrolling slide got a morph slot: assets/critical.css gives the auto-height
     treatment to BOTH scroll slides in one rule --

       body.page-index .shopify-section.section-our-story.ls-active,
       body.page-index .shopify-section.section-flodesk-newsletter.ls-active {
         position: relative; height: auto; top/left/right: auto;
         align-items: flex-start;
       }

     -- so the set that changes layout on activation IS the set of scroll slides.
     Asking isScrollSlide() cannot drift from that rule; a per-slot boolean can,
     and did.

     WHAT IT COST: the newsletter slot said isOurStory: false, so its card was
     measured in the INACTIVE layout (absolute, height 100%, centred) while the
     box was aiming at where it would sit once ACTIVE (relative, auto height,
     top-aligned). The box travelled to the wrong rect, the class swap then put
     the real card somewhere else, and the whole thing read as the newsletter
     popping in whole -- which is exactly what Celeste reported. Not the fonts
     wait, which measures 0.1ms once settled. */
  /* SLOTS ARE RESOLVED AT MORPH TIME, not at init.

     Product Card's active card changes between visits -- paging is click-only UI
     local to that section, independent of the slider -- so a slot that captured
     `.pc__card--active` at init would morph whichever card happened to be showing
     when the page loaded. buildProductCardTl already rebuilds for exactly this
     reason. A field may therefore be a FUNCTION, evaluated once per morph; plain
     arrays pass through untouched, so the existing slots are unaffected.

     Returns a shallow copy, so one morph cannot leave resolved element references
     behind on the stored slot for the next. */
  function resolveSlot(slot) {
    if (!slot) return slot;
    var out = { idx: slot.idx, section: slot.section };
    ['cards', 'sourceUnion', 'content', 'contentIn', 'contentLead', 'contentTrail',
     'crossFade', 'growIn', 'showAfterGrow'].forEach(function (k) {
      var v = slot[k];
      out[k] = (typeof v === 'function') ? (v() || []) : v;
    });
    out.card = slot.card || (out.cards && out.cards.length ? out.cards[0] : null);
    return out;
  }

  /* What fades in on ARRIVAL, which is not always what fades out on DEPARTURE.
     Reviews is both: leaving, its box keeps its shape and only its contents go
     (`content`); arriving, the header card and the carousel come in too, and the
     header belongs to no box at all. One field could not mean both. */
  function inContentOf(slot) {
    return (slot.contentIn && slot.contentIn.length) ? slot.contentIn : (slot.content || []);
  }

  /* A slot's morphable boxes, PRIMARY FIRST. `card` stays the primary and is
     what the rest of the chain measures content against; `cards` is the full
     list. Declaring only `card` still works and yields a one-element list. */
  function cardsOf(slot) {
    if (slot.cards && slot.cards.length) return slot.cards;
    return slot.card ? [slot.card] : [];
  }

  /* ALL of a slot's boxes in ONE layout pass. Measuring them one at a time would
     enter and leave the simulated active layout once per box -- the same answer,
     several times the work, and several more chances for something to observe a
     state that only exists between those two lines. */
  function measureCardRects(slot, els) {
    function take() {
      return els.map(function (el) {
        return { rect: rectOf(el), radius: getComputedStyle(el).borderRadius };
      });
    }
    if (isScrollSlide(slot.idx) && !slot.section.classList.contains('ls-active')) {
      return withActiveLayout(slot.section, take);
    }
    return take();
  }

  /* A MORPH SOURCE IS NOT ALWAYS AN ELEMENT.

     Leaving Our Story, what the visitor is looking at is the photograph -- and on
     a tall desktop, where the slide does not scroll at all, the card above it as
     well. They are siblings inside .os__col, not nested, so no single element's
     rect covers what is on screen. The box that stands in for them is the UNION
     of their VISIBLE parts.

     CLIPPED TO THE VIEWPORT, deliberately. Measured at the bottom of Our Story on
     a 677px viewport, the card sits at top -950 and the photo runs from -205 to
     785: an unclipped union would start the box 950px above the fold and 108px
     below it, so it would fly in from off screen rather than grow out of what is
     there. Clipping makes the box start exactly where the eye already is.

     An element that is entirely off screen contributes NOTHING rather than
     dragging the union out to meet it -- that is the card's case at the bottom,
     and it is what makes the photo-only and both-visible cases one rule instead
     of two.

     Returns [] when nothing is visible at all, which the caller treats as "no
     boxes" and falls through to the plain swap rather than morphing a zero rect. */
  function sourceBoxesOf(slot) {
    var union = slot.sourceUnion;
    if (union && union.length) {
      /* CLIPPED TO THE CONTAINER'S VIEWPORT RECT, not [0, clientHeight] --
         .page-content is position:fixed at top 108 (the nav sits above it),
         so clipping to [0, clientHeight] mixed coordinate spaces and put the
         box 108px too high, over the nav, and 108px short at the bottom. */
      var cr = container.getBoundingClientRect();
      var top = Infinity, left = Infinity, right = -Infinity, bottom = -Infinity, seen = 0;
      union.forEach(function (el) {
        if (!el) return;
        var r = el.getBoundingClientRect();
        var t = Math.max(r.top, cr.top), b = Math.min(r.bottom, cr.bottom);
        if (b - t <= 0) return;
        seen++;
        top    = Math.min(top, t);
        bottom = Math.max(bottom, b);
        left   = Math.min(left, r.left);
        right  = Math.max(right, r.right);
      });
      if (!seen) return [];
      return [{
        rect: { top: top, left: left, width: right - left, height: bottom - top },
        radius: getComputedStyle(union[0]).borderRadius,
        els: union.filter(Boolean)
      }];
    }
    var els = cardsOf(slot);
    return measureCardRects(slot, els).map(function (m, i) {
      return { rect: m.rect, radius: m.radius, els: [els[i]] };
    });
  }

  /* THE DESTINATION IS MEASURED WHERE IT WILL BE, NOT WHERE IT IS -- the two
     halves of a morph need DIFFERENT scroll states (source as the visitor
     left it, destination as if osExit() had already reset it, since it will
     before the real element shows). Measured leaving Our Story: the product
     card read top -860 while still scrolled, top 230 once reset -- 1090px of
     error, flying the box entirely above the screen. Synchronous, so no
     paint happens between reset and restore. */
  function withScrollReset(fn) {
    if (!container.scrollTop) return fn();
    var saved = container.scrollTop;
    container.scrollTop = 0;
    var out = fn();
    container.scrollTop = saved;
    return out;
  }

  function removeMorphBoxes() {
    _morphBoxes.forEach(function (b) {
      if (b && b.parentNode) b.parentNode.removeChild(b);
    });
    _morphBoxes = [];
  }

  /* Failsafe sweep — called before every morph attempt so a leftover box or
     tween from a previous (or interrupted) run can never survive into the
     next transition. Also restores a card that was left visibility:hidden
     mid-morph, so an interrupted run can never leave a permanently-invisible
     card behind either. */
  function killMorph() {
    if (_morphTween) {
      _morphTween.kill();
      _morphTween = null;
    }
    removeMorphBoxes();
    if (_morphHiddenEls.length) {
      gsap.set(_morphHiddenEls, { clearProps: 'visibility' });
      _morphHiddenEls = [];
    }
  }

  function doMorphTransition(fromIdx, toIdx) {
    var from = resolveSlot(MORPH_SLIDES[fromIdx]);
    var to   = resolveSlot(MORPH_SLIDES[toIdx]);

    /* Safety net: if either card is missing for any reason, fall back to the
       plain instant swap rather than morphing against a null element. */
    if (!from || !to || !from.card || !to.card) {
      killSlide(slides[fromIdx]);
      if (isScrollSlide(fromIdx)) osExit();
      slides[fromIdx].classList.remove('ls-active');
      current = toIdx;
      slides[toIdx].classList.add('ls-active');
      if (isScrollSlide(toIdx)) osEnter();
      scheduleUnlock(ADVANCE_COOLDOWN * 1000);
      return;
    }

    killMorph();
    if (typeof killCrossfade === 'function') killCrossfade();
    killSlide(from.section);
    killSlide(to.section);
    if (timelines[fromIdx]) timelines[fromIdx].pause();
    if (timelines[toIdx])   timelines[toIdx].pause();

    /* osResetState now, osExit LATER -- same split as doCrossfade, same reason.
       Calling osExit() here (before boxes are measured) would snap the
       viewport to the slide's top before the transition starts and measure
       the source box at its post-reset position instead of where the visitor
       can see it. Inert for every pair today (Reviews/Product Card aren't
       scroll slides); a prerequisite for morphing OUT of Our Story, where the
       card can be 950px above the fold. osResetState() stays here since it
       only clears settle timers/latches, touching neither class nor scroll. */
    if (isScrollSlide(fromIdx)) osResetState();

    /* Which synthetic box stands in for which DESTINATION element -- written
       by startBoxMorph, read by finishBoxPhase, kept here so the two phases
       can't disagree about the pairing. */
    var morphPairs = [];

    /* Hoisted out of startBoxMorph so the grow (duration/travel) and the
       settle (content timings) can't disagree about which pair they animate. */
    var opts = morphOptionsFor(fromIdx, toIdx) || { travel: true };

    function startBoxMorph() {
      /* PAIRED BY POSITION, primary first, zipped to the SHORTER list -- extras
         on either side are the choreography's business, not the morph's
         (Reviews' controls box has no Newsletter counterpart; its header card
         has no source). Length mismatch is legal, so it's announced rather
         than asserted -- silently morphing fewer boxes than intended is
         exactly this file's usual failure shape. */
      var fadeIn = opts.boxFadeIn || 0;
      var fromBoxes = sourceBoxesOf(from);
      var toCards   = cardsOf(to);
      var n = Math.min(fromBoxes.length, toCards.length);
      if (fromBoxes.length !== toCards.length && window.console && console.info) {
        console.info('[landing-slider] morph ' + fromIdx + ' -> ' + toIdx + ': ' +
          fromBoxes.length + ' source boxes, ' + toCards.length + ' destination; ' +
          'morphing ' + n + '. The remainder is handled by the choreography.');
      }

      /* Source first, with the scroll intact; destination second, with it
         reset. Order is load-bearing -- see withScrollReset. */
      var toM = withScrollReset(function () {
        return measureCardRects(to, toCards.slice(0, n));
      });

      /* ONE timeline, so finishBoxPhase fires when EVERY box has landed --
         onComplete on a single tween would fire at the FIRST, and boxes of
         different sizes don't finish together even at equal durations. */
      var growTl = gsap.timeline({ onComplete: finishBoxPhase });

      for (var i = 0; i < n; i++) {
        var box = document.createElement('div');
        box.className = 'ls-morph-box';
        box.setAttribute('aria-hidden', 'true');
        document.body.appendChild(box);
        _morphBoxes.push(box);

        gsap.set(box, {
          position: 'fixed',
          top: fromBoxes[i].rect.top,
          left: fromBoxes[i].rect.left,
          width: fromBoxes[i].rect.width,
          height: fromBoxes[i].rect.height,
          borderRadius: fromBoxes[i].radius,
          background: 'var(--color-landing-card-bg, #ECEAE3)',
          /* UNDER THE NAV, like the real cards -- header group is a fixed
             z-index:100 sibling, .page-content is z-index:auto beneath it, so
             50 sits above every slide and below the nav. At 1000 it painted
             over the nav (invisible until a box reached the top of screen). */
          zIndex: 50,
          pointerEvents: 'none'
        });

        /* A source box may stand in for MORE than one element (the union case
           covers Our Story's card and photograph together). Registered for
           cleanup now whether or not hidden yet, so an interrupted run still
           restores them. */
        fromBoxes[i].els.forEach(function (el) { _morphHiddenEls.push(el); });
        morphPairs.push({ box: box, toEl: toCards[i] });

        if (fadeIn) {
          gsap.set(box, { opacity: 0 });
          growTl.to(box, { opacity: 1, duration: fadeIn, ease: 'none' }, 0);
          /* HIDDEN ONLY ONCE THE BOX IS OPAQUE -- hiding at t=0 (the no-fade
             path) would take the photograph away before anything covered it. */
          growTl.call((function (els) {
            return function () { gsap.set(els, { visibility: 'hidden' }); };
          })(fromBoxes[i].els), null, fadeIn);
        } else {
          gsap.set(fromBoxes[i].els, { visibility: 'hidden' });
        }

        /* TWO SHAPES per pair (see BOX_MORPH_FORWARD): travel grows to the
           destination's SIZE around the source's own centre, then moves as a
           separate beat (finishBoxPhase) -- reads as becoming; direct animates
           size+position together, for pairs where growing around centre would
           send an edge the wrong way first. */
        growTl.to(box, {
          top:  opts.travel
            ? (fromBoxes[i].rect.top  + fromBoxes[i].rect.height / 2) - toM[i].rect.height / 2
            : toM[i].rect.top,
          left: opts.travel
            ? (fromBoxes[i].rect.left + fromBoxes[i].rect.width  / 2) - toM[i].rect.width  / 2
            : toM[i].rect.left,
          width: toM[i].rect.width,
          height: toM[i].rect.height,
          borderRadius: toM[i].radius,
          duration: opts.duration || MORPH_DURATION,
          /* power1.in when a travel beat follows: the two beats meet at a hard
             boundary, so what joins them is VELOCITY, not timing -- an inOut
             ease would decelerate to a full stop, and the travel's own
             power2.out would start from a second stop, reading as two
             movements instead of one arc. A direct morph has nothing after
             it, so it settles on its own (power2.inOut). */
          ease: opts.travel ? 'power1.in' : 'power2.inOut',
          onInterrupt: morphInterrupted('box-grow')
          /* All boxes at 0: a pair's boxes move as one unit. */
        }, 0);
      }

      /* Counterpart of the box fading in: whatever it replaces fades out across
         the same window, so the photograph turns into eggshell rather than
         vanishing. Added once, not per box -- the list belongs to the slide. */
      if (fadeIn && from.crossFade && from.crossFade.length) {
        growTl.to(from.crossFade, { opacity: 0, duration: fadeIn, ease: 'none' }, 0);
      }

      _morphTween = growTl;
      /* No boxes to morph: don't rely on GSAP's zero-duration behaviour to
         complete an empty timeline on the next tick -- advance explicitly. */
      if (!n) growTl.progress(1);
    }

    /* EVERY tween in this chain gets one of these -- a killed tween doesn't
       fire onComplete, so before this an interruption left NO trace: the page
       just stopped, `current` unadvanced, nothing scheduled to unlock, only
       the 12s watchdog to recover. Took four hours to diagnose on 2026-08-27
       precisely because there was nothing to read. Doesn't repair anything --
       the safety net does that -- just names the beat instead of silence. */
    function morphInterrupted(beat) {
      return function () {
        if (window.console && console.warn) {
          console.warn('[landing-slider] morph beat "' + beat + '" was interrupted (' +
            fromIdx + ' -> ' + toIdx + '). The chain will not complete on its own; ' +
            'the safety net should arrive shortly.');
        }
      };
    }

    /* THE MORPH RESTORES WHAT THE MORPH FADED -- nothing else can be relied on
       to. killSlide() reverts inline styles for a FIXED SELECTOR LIST, so a
       slot fading something outside that list keeps its opacity forever (the
       reviews slot faded .cr__card--review's Judge.me children, which
       killSlide's `.cr__card` selector doesn't match -- the card stayed
       permanently blank on the way back). A slot's fade list and a separate
       restore list drifting apart by hand is the same failure shape as most
       of this week's bugs, so the slot restores itself from its own arrays
       instead. clearProps rather than opacity:1, since resting opacity is
       whatever CSS says and asserting 1 would override a legitimately
       translucent element. */
    function restoreFaded(slot) {
      if (!slot) return;
      if (slot.contentLead && slot.contentLead.length) {
        gsap.set(slot.contentLead, { clearProps: 'opacity' });
      }
      if (slot.content && slot.content.length) {
        gsap.set(slot.content, { clearProps: 'opacity' });
      }
      if (slot.growIn && slot.growIn.length) {
        gsap.set(slot.growIn, { clearProps: 'transform,scaleX,transformOrigin' });
      }
      if (slot.showAfterGrow && slot.showAfterGrow.length) {
        gsap.set(slot.showAfterGrow, { clearProps: 'opacity' });
      }
      if (slot.crossFade && slot.crossFade.length) {
        gsap.set(slot.crossFade, { clearProps: 'opacity' });
      }
      if (slot.contentIn && slot.contentIn.length) {
        gsap.set(slot.contentIn, { clearProps: 'opacity' });
      }
      if (slot.contentTrail && slot.contentTrail.length) {
        gsap.set(slot.contentTrail, { clearProps: 'opacity' });
      }
    }

    function finishBoxPhase() {
      _morphTween = null;

      /* MEASURED, not carried over from startBoxMorph's arithmetic -- each lift
         is the gap between where THAT box ended and where its real element
         sits, absorbing any drift (late font, mid-morph resize) instead of
         showing it as a jump. Read before removeMorphBoxes(). */
      var landed = morphPairs.map(function (pr) {
        return { toEl: pr.toEl, boxRect: pr.box ? rectOf(pr.box) : null, lift: 0 };
      });

      /* VISIBILITY ONLY -- clearProps:'all' would wipe .pc__card's own inline
         --pc-image-scale/--pc-image-offset-y (product-card.liquid), snapping a
         3.2x product image down to 1x. `visibility` is the only thing the
         morph ever sets on these elements, so it's the only thing to restore.
         Survived unnoticed until a .pc__card was first used as a morph
         source -- broke the image, not the morph, so it surfaced a slide away
         from the change. */
      if (_morphHiddenEls.length) gsap.set(_morphHiddenEls, { clearProps: 'visibility' });
      _morphHiddenEls = [];
      /* Outgoing slide is done animating and about to be hidden -- restore it
         NOW, off screen, so returning later finds it whole. */
      restoreFaded(from);
      /* Deferred from the top of doMorphTransition -- see the note there. */
      if (isScrollSlide(fromIdx)) osExit();
      from.section.classList.remove('ls-active');

      to.section.classList.add('ls-active');
      current = toIdx;
      if (isScrollSlide(toIdx)) osEnter();

      cleanAnimClasses(from.section);
      cleanAnimClasses(to.section);

      removeMorphBoxes();

      landed.forEach(function (L) {
        /* Scoped for the same reason as the restore above: clearProps:'all' would
           take any inline custom properties the destination owns with it. */
        gsap.set(L.toEl, { clearProps: 'opacity,transform,translate,rotate,scale,transformOrigin,clipPath,visibility', opacity: 1 });
      });

      /* THE LIFT. .ls-active has just landed, which changes the newsletter's
         --ns-offset via a MutationObserver -- a microtask that hasn't run yet
         in this same task, so measuring now would read the pre-activation
         position. Force it first. Then place the card where the box ended and
         tween it home -- the card carries the travel itself so its children
         (the contents) fade in DURING the move for free. */
      if (typeof to.section.__lsRelayout === 'function') to.section.__lsRelayout();
      landed.forEach(function (L) {
        L.lift = L.boxRect ? (L.boxRect.top - L.toEl.getBoundingClientRect().top) : 0;
        gsap.set(L.toEl, { y: L.lift });
      });

      /* Beat 5, started here rather than chained after beat 4 -- the one
         deliberate overlap. Does NOT gate finishMorph: the transition is over
         when the content lands, not the footer. */
      if (to.contentTrail && to.contentTrail.length) {
        gsap.set(to.contentTrail, { opacity: 0 });
        gsap.to(to.contentTrail, {
          opacity: 1,
          duration: TRAIL_IN_DURATION,
          /* Offset from the CONTENT, not this moment -- content starts
             CONTENT_IN_OFFSET into the travel; left bare this would drift
             forward by that much and land under a still-moving card. */
          delay: CONTENT_IN_OFFSET + TRAIL_IN_OFFSET,
          ease: 'power2.out',
          onInterrupt: morphInterrupted('trail-in'),
          onComplete: function () { gsap.set(to.contentTrail, { clearProps: 'opacity' }); }
        });
      }

      /* A TIMELINE, not two chained tweens -- finishMorph must fire when BOTH
         the travel and the contents are done, and neither is reliably longer
         (tuned independently). Built fresh every morph, never cached -- same
         killTweensOf rule as killSlide. */
      var settleTl = gsap.timeline({ onComplete: finishMorph });

      landed.forEach(function (L) {
        settleTl.to(L.toEl, {
          y: 0,
          duration: MORPH_SETTLE_DURATION,
          ease: 'power2.out',
          onInterrupt: morphInterrupted('settle')
        }, 0);
      });

      /* The panel opens from its own centre, then its dots appear together.
         Both sit in the settle timeline so finishMorph waits for whichever
         lane finishes last. */
      if (to.growIn && to.growIn.length) {
        gsap.set(to.growIn, { scaleX: 0, transformOrigin: '50% 50%' });
        settleTl.to(to.growIn, {
          scaleX: 1,
          duration: GROW_IN_DURATION,
          ease: 'power2.out',
          onInterrupt: morphInterrupted('grow-in')
        }, 0);
      }
      if (to.showAfterGrow && to.showAfterGrow.length) {
        gsap.set(to.showAfterGrow, { opacity: 0 });
        /* No stagger, deliberately: the dots are one control, and stepping through
           five of them read as fussy against a card that had already landed. */
        settleTl.to(to.showAfterGrow, {
          opacity: 1,
          duration: SHOW_AFTER_GROW,
          ease: 'power2.out',
          onInterrupt: morphInterrupted('show-after-grow')
        }, GROW_IN_DURATION);
      }

      var toIn = inContentOf(to);
      if (toIn.length) {
        /* OPACITY ONLY, no y -- these are the card's own children, so they
           ALREADY carry its travel; adding a 14px rise on top uncovered a
           sliver of card background above the media for the whole reveal
           (worst on mobile). Fading in place matches the image's motion to
           the card's; the sequence still reads via the stagger. Don't
           reintroduce y without giving the media a flush first frame. */
        gsap.set(toIn, { opacity: 0 });
        settleTl.to(toIn, {
          opacity: 1,
          duration: opts.contentDuration || TEXT_IN_DURATION,
          /* Arrives in DOM order (newsletter: media, then copy) -- the
             sequence Celeste asked for comes free from the markup. */
          stagger: (opts.contentStagger != null) ? opts.contentStagger : CONTENT_IN_STAGGER,
          ease: 'power2.out',
          onInterrupt: morphInterrupted('content-in')
        }, (opts.contentOffset != null) ? opts.contentOffset : CONTENT_IN_OFFSET);
      }

      _morphTween = settleTl;
    }

    /* SAFETY NET for the whole chain -- runPhases -> startBoxMorph ->
       finishBoxPhase -> finishMorph is beats joined by onComplete, and the
       ONLY scheduleUnlock is in the last one; a killed tween never fires
       onComplete, so any break leaves current unadvanced with nothing
       scheduled to unlock (observed live 2026-08-27, recoverable only by the
       12s watchdog). THIS ARRIVES RATHER THAN MERELY UNLOCKING -- unlocking a
       transition that didn't happen leaves the visitor on the old slide with
       working input, a different bug. Idempotent via current === toIdx, and
       cancelled by finishMorph on the normal path. Recovered from
       scratchpad/wip-flag-and-safety-net.patch. */
    var _morphSafety = null;
    function armMorphSafety() {
    _morphSafety = setTimeout(function () {
      _morphSafety = null;
      if (current === toIdx) return;   /* arrived normally; nothing to do */

      killMorph();
      /* Every box the morph hid or moved, not just the primary. _morphHiddenEls
         is the authoritative list of what was hidden; cardsOf(to) covers the
         destinations, which may carry a lift transform if the net fires during
         the settle. Restoring only `card` left any second box invisible. */
      if (_morphHiddenEls.length) gsap.set(_morphHiddenEls, { clearProps: 'visibility' });
      _morphHiddenEls = [];
      restoreFaded(from);   /* same reason as the normal path -- see restoreFaded */
      if (isScrollSlide(fromIdx)) osExit();
      from.section.classList.remove('ls-active');
      to.section.classList.add('ls-active');
      current = toIdx;
      if (isScrollSlide(toIdx)) osEnter();
      cleanAnimClasses(from.section);
      cleanAnimClasses(to.section);
      var _toCards = cardsOf(to);
      if (_toCards.length) gsap.set(_toCards, { clearProps: 'opacity,transform,translate,rotate,scale,transformOrigin,clipPath,visibility', opacity: 1 });
      if (to.growIn && to.growIn.length) {
        gsap.set(to.growIn, { clearProps: 'transform,scaleX,transformOrigin' });
      }
      if (to.showAfterGrow && to.showAfterGrow.length) {
        gsap.set(to.showAfterGrow, { clearProps: 'opacity' });
      }
      var _toIn = inContentOf(to);
      if (_toIn.length) {
        /* No y here either -- clearProps already drops any transform, and
           asserting y:0 would re-add an inline one the morph no longer sets. */
        gsap.set(_toIn, { clearProps: 'opacity,transform,translate,rotate,scale,transformOrigin,clipPath,visibility', opacity: 1 });
      }
      /* The trail lives outside the slide, so nothing else would restore it. */
      if (to.contentTrail && to.contentTrail.length) {
        gsap.set(to.contentTrail, { clearProps: 'opacity' });
      }
      if (window.console && console.warn) {
        console.warn('[landing-slider] morph chain did not complete; arrived at slide ' +
          toIdx + ' directly. The interrupted beat named above is the real bug.');
      }
      finishMorph();
    }, MORPH_SAFETY_MS);
    }

    function finishMorph() {
      _morphTween = null;
      if (_morphSafety) {
        clearTimeout(_morphSafety);
        _morphSafety = null;
      }
      /* ANY scrolling destination, not just Our Story. `ls-scrollable` is the
         ONLY thing that makes .page-content overflow-y:auto, so a scrolling
         slide arriving without it renders fine but can't be scrolled -- on
         the newsletter that strands the signup form below the fold with
         nothing in the console. finishBoxPhase()'s osEnter() does NOT cover
         this (it's only scrollTop = 0, never the class). Set HERE rather than
         derived centrally from isScrollSlide(current), since the class also
         means "entrance has finished" (osScrollEnabled() reads that to absorb
         input mid-animation) -- this is the point both become true together. */
      if (isScrollSlide(toIdx)) {
        document.body.classList.add('ls-scrollable');
        osResetState();
        _osTopSettleTimer = setTimeout(function () {
          _osTopSettleTimer = null;
          /* current === toIdx, not === OS_IDX: this arms the latch for whichever
             scrolling slide we just landed on, and only if we are still on it. */
          if (current === toIdx && osAtTop()) {
            _osTopSettled = true;
          }
        }, SETTLE_QUIET_MS);
      }
      scheduleUnlock(ADVANCE_COOLDOWN * 1000);
    }

    /* Phase 1 -- the outgoing card's own contents leave, and the box stays. */
    /* Phase 0 -- OPTIONAL. A slot may declare `contentLead`: elements that leave
       BEFORE its content does, so the departure reads as two beats rather than
       one. Reviews -> newsletter needs it (the header card and the dots strip go
       first, then the review box's contents, then the box itself moves); intro
       <-> Our Story does not declare it and its chain is unchanged in shape.

       OVERLAPPED, not chained. These two beats were joined on onComplete like
       every other beat in this path, which put CONTENT_LEAD_OUT + TEXT_OUT_DURATION
       = 0.45s between the visitor's gesture and the box starting to move. Nothing
       is on screen doing anything for most of that, so it read as a wait rather
       than as choreography. The content now starts CONTENT_OUT_OVERLAP into the
       lead-out, which keeps both beats legible -- the header and dots still
       visibly go first -- while cutting the out-phase to 0.28s.

       A TIMELINE rather than two tweens, for the same reason the settle is one:
       startBoxMorph must fire when BOTH lanes are done and neither is reliably
       the longer, so hanging it off whichever looks longer today breaks on the
       next retime. Built fresh per morph, never cached -- see killSlide. */
    function runPhases() {
      /* Armed HERE, not at dispatch. MORPH_SAFETY_MS is a budget for the CHAIN,
         and the chain starts on this line -- doMorphTransition may have been
         called an arbitrary time earlier, since it waits on document.fonts.ready
         first. Budgeting from the request rather than the start means the net
         can fire before beat one has run and "arrive" instantly, which looks
         exactly like the transition slamming in whole.

         In practice that gap is ~0: fonts.ready settles in 0.1ms once loaded,
         and no morph can happen before slide 3, by which point all 37 faces are
         long since loaded. So this is correctness, not a bug fix -- the pop had a
         different cause (see the measureCardRect commit). But an uncounted wait
         inside a timed budget is wrong whatever it currently measures, and a
         slow cold load is exactly when it would stop being zero. */
      armMorphSafety();
      var hasLead = !!(from.contentLead && from.contentLead.length);
      if (!hasLead && !from.content.length) {
        startBoxMorph();
        return;
      }

      var outTl = gsap.timeline({ onComplete: startBoxMorph });
      if (hasLead) {
        outTl.to(from.contentLead, {
          opacity: 0,
          duration: CONTENT_LEAD_OUT,
          ease: 'power2.in',
          onInterrupt: morphInterrupted('lead-out')
        }, 0);
      }
      if (from.content.length) {
        outTl.to(from.content, {
          opacity: 0,
          duration: TEXT_OUT_DURATION,
          ease: 'power2.in',
          onInterrupt: morphInterrupted('content-out')
        }, hasLead ? CONTENT_OUT_OVERLAP : 0);
      }
      _morphTween = outTl;
    }

    /* FONTS, BUT NEVER FOR LONG -- an unceilinged document.fonts.ready wait
       held `locked` for the whole wait (measured ~730ms on a cold load) and,
       worse, was UNBOUNDED: a promise that never resolves means the chain
       never starts and the safety net (armed INSIDE runPhases) never arms
       either, a permanent stall past killSlide having already run. Whichever
       of the promise or FONTS_WAIT_MAX_MS comes first now wins; the fonts are
       only a nicety (a few px of text-wrap accuracy), not worth a locked
       page. */
    var _phasesStarted = false;
    function startPhases() {
      if (_phasesStarted) return;
      _phasesStarted = true;
      runPhases();
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(startPhases);
      setTimeout(startPhases, FONTS_WAIT_MAX_MS);
    } else {
      startPhases();
    }
  }

  /* Story <-> Product, Product <-> Reviews CROSSFADE transitions -- outgoing
     slide fades out as a whole section, THEN incoming fades in, then its text
     follows (sequential, same phase philosophy as the morph). No shared
     element to measure -- each slide fades in place at its already-correct
     position, so entering reuses the EXISTING entrance timelines. Our Story
     is the exception: it only ENTERS via crossfade (from Product Card) and
     must land already-settled at the BOTTOM scroll position, so it skips
     timelines[OS_IDX] and just fades the already-visible section in. It only
     EXITS via crossfade forward, a plain osExit(). */

  var CROSSFADE_PAIRS = [];
  /* OS<->PC and PC<->CR are no longer symmetric -- forward is now a morph for
     both, so only the backward leg stays a crossfade (CROSSFADE_BACK_ONLY). */

  /* ONE-WAY: crossfade in the listed direction only. Reviews -> newsletter is
     the box morph; newsletter -> reviews had no transition at all until
     Celeste chose to match it to OS<->PC/PC<->CR rather than author a second
     bespoke reverse morph (2026-08-28). SAFE AGAINST THE MORPH since goTo
     tests isBoxMorphForward first and that only matches CR -> NL -- the two
     can never both claim a transition. */
  var CROSSFADE_BACK_ONLY = [];
  if (CR_IDX !== -1 && NL_IDX !== -1) CROSSFADE_BACK_ONLY.push([NL_IDX, CR_IDX]);
  if (PC_IDX !== -1 && CR_IDX !== -1) CROSSFADE_BACK_ONLY.push([CR_IDX, PC_IDX]);
  if (OS_IDX !== -1 && PC_IDX !== -1) CROSSFADE_BACK_ONLY.push([PC_IDX, OS_IDX]);

  function isCrossfadePair(a, b) {
    return CROSSFADE_PAIRS.some(function (p) {
      return (p[0] === a && p[1] === b) || (p[1] === a && p[0] === b);
    }) || CROSSFADE_BACK_ONLY.some(function (p) {
      return p[0] === a && p[1] === b;
    });
  }

  /* THE FOOTER IS NOT PART OF ANY SLIDE, so fading the outgoing SECTION does
     not fade it -- it's a sibling of the slides inside .page-content,
     revealed by a DISPLAY flip keyed on the newsletter's .ls-active
     (assets/critical.css, `.section-flodesk-newsletter.ls-active ~
     .shopify-section-group-footer-group`). Left out of the fade it stays
     opaque for the whole 300ms then hard-cuts when the class comes off.
     Read off the morph slot's contentTrail rather than re-querying, so the
     set that fades OUT here is by construction what beat 5 fades IN. */
  function trailOf(idx) {
    var slot = MORPH_SLIDES[idx];
    return (slot && slot.contentTrail && slot.contentTrail.length) ? slot.contentTrail : null;
  }

  var _cfTween = null; /* whichever tween is currently driving the crossfade */

  /* Failsafe sweep, mirrors killMorph() -- called before every crossfade so
     an interrupted previous run can't leave a section stuck mid-opacity.
     Cross-cutting insurance with killMorph() (each sweeps the other's state
     too); costs nothing since `locked` normally prevents the two overlapping. */
  function killCrossfade() {
    if (_cfTween) {
      _cfTween.kill();
      _cfTween = null;
    }
    slides.forEach(function (s) { gsap.set(s, { clearProps: 'opacity' }); });
    /* Trails live OUTSIDE the slides, so the sweep above misses them and an
       interrupted fade would strand the footer at opacity 0 permanently. */
    Object.keys(MORPH_SLIDES).forEach(function (k) {
      var t = trailOf(k);   /* object keys are strings; MORPH_SLIDES[3] and ['3'] are one slot */
      if (t) gsap.set(t, { clearProps: 'opacity' });
    });
  }

  function enterOsAtBottomSettled() {
    var section = slides[OS_IDX];
    if (timelines[OS_IDX]) timelines[OS_IDX].pause();
    killSlide(section); /* resets card/photo/text to their natural fully-visible resting state */

    document.body.classList.add('ls-scrollable');
    container.scrollTop = container.scrollHeight - container.clientHeight;

    osResetState();
    _osBottomSettleTimer = setTimeout(function () {
      _osBottomSettleTimer = null;
      if (current === OS_IDX && osAtBottom()) {
        _osBottomSettled = true;
      }
    }, SETTLE_QUIET_MS);

    gsap.set(section, { opacity: 0 });
    _cfTween = gsap.to(section, {
      opacity: 1,
      duration: CROSSFADE_IN,
      ease: 'power2.out',
      force3D: true,
      onComplete: function () {
        _cfTween = null;
        gsap.set(section, { clearProps: 'opacity' });
        scheduleUnlock(ADVANCE_COOLDOWN * 1000);
      }
    });
  }

  function enterViaEntranceTimeline(toIdx) {
    var toSection = slides[toIdx];
    cleanAnimClasses(toSection);
    /* Product Card's active card can change between visits (click-only
       paging, independent of the slider) -- rebuild fresh on every entry so
       it animates whichever card is active now. */
    if (toIdx === PC_IDX) timelines[PC_IDX] = buildProductCardTl();
    /* Reviews needs the same treatment, genuinely: killSlide()'s
       killTweensOf() empties timelines[CR_IDX] (built once at init) from the
       first departure onward, so arriving back restarted a 0-duration
       timeline -- visible but not animating, which is why it never looked
       like a bug. Same rule the hero's return timeline needed. */
    if (toIdx === CR_IDX) timelines[CR_IDX] = buildCustomerReviewsTl();
    if (toIdx === 0 && _heroEntrancePlayed) timelines[0] = buildHeroReturnTl();
    var tl = timelines[toIdx];
    if (tl) {
      /* Arm here as well as in goTo -- this is the crossfade's landing point
         (OS<->PC, PC<->CR), reached from doCrossfade's onComplete, and goTo
         returned early for those pairs without running its own arming. */
      _entranceSkippable = true;
      tl.restart(true);
      scheduleUnlock((tl.duration() + ADVANCE_COOLDOWN) * 1000);
    } else {
      _entranceSkippable = false;
      scheduleUnlock(ADVANCE_COOLDOWN * 1000);
    }
  }

  function doCrossfade(fromIdx, toIdx) {
    killMorph();
    killCrossfade();
    killSlide(slides[fromIdx]);
    if (timelines[fromIdx]) timelines[fromIdx].pause();
    if (timelines[toIdx])   timelines[toIdx].pause();

    /* osExit() must NOT run yet if fromIdx is Our Story -- .ls-active doesn't
       come off until this fade's onComplete, and resetting scrollTop while
       Our Story is still tall/in-flow/opaque would snap the viewport from
       "bottom of photo" to "top of card" instantly, masking the fade as "it's
       just scrolling". Deferred to the same moment .ls-active comes off. */
    if (isScrollSlide(fromIdx)) osResetState();

    var fromSection = slides[fromIdx];
    var toSection   = slides[toIdx];
    var fromTrail   = trailOf(fromIdx);   /* the footer, leaving the newsletter — see trailOf */

    _cfTween = gsap.to(fromTrail ? [fromSection].concat(fromTrail) : fromSection, {
      opacity: 0,
      duration: CROSSFADE_OUT,
      ease: 'power2.in',
      force3D: true,
      onComplete: function () {
        _cfTween = null;
        if (isScrollSlide(fromIdx)) osExit();
        fromSection.classList.remove('ls-active');
        gsap.set(fromSection, { clearProps: 'opacity' });
        /* After the class swap, deliberately: clearing while the footer is
           still displayed would flash it back to full opacity for a frame. */
        if (fromTrail) gsap.set(fromTrail, { clearProps: 'opacity' });
        cleanAnimClasses(fromSection);

        toSection.classList.add('ls-active');
        current = toIdx;

        if (toIdx === OS_IDX) {
          enterOsAtBottomSettled();
        } else {
          enterViaEntranceTimeline(toIdx);
        }
      }
    });
  }

  /* Header visibility: hidden on the intro slide, visible after it. Driven
     from the active index, not any one transition's choreography -- three
     separate paths reach slide 0's neighbours (morph/crossfade/plain swap),
     the header is page chrome not part of either slide, and goTo() is the
     one funnel all of them (including REDUCED_MOTION) already route through.
     Called before the branch below so the fade overlaps the transition
     rather than queuing behind it. _headerShown starts null so the first
     call always writes, rather than assuming a CSS-applied state. */
  var headerGroup = document.querySelector('.shopify-section-group-header-group');
  var headerCard  = headerGroup && headerGroup.querySelector('.header__card');

  /* Glass behind the nav (sections/landing-nav-scrim.liquid) -- a sibling of
     the header, not a child, so it needs its own explicit fade here. */
  var navScrim = document.querySelector('.nav-scrim');
  var _headerShown = null;

  /* Merchant's "When the menu appears" choice on the Intro section, read from
     a data attribute since this static asset can't see Liquid. 'scroll' is
     the original behaviour; 'animation' additionally reveals the nav when the
     intro card finishes. Anything unexpected (including a missing element)
     falls back to 'scroll', which depends on nothing but slide index and so
     can't strand the page without a nav. */
  var _introEl   = document.querySelector('.ie-intro');
  var NAV_REVEAL = _introEl && _introEl.dataset.navReveal === 'animation'
    ? 'animation'
    : 'scroll';

  /* Set by the intro timeline's onComplete, only under NAV_REVEAL ===
     'animation'. Never reset -- returning to slide 0 replays the entrance,
     but the nav has already been introduced and taking it away would read as
     a bug. If onComplete never fires, the page is NOT stranded: goTo's
     scheduleUnlock runs regardless and advancing reveals the nav via the
     idx !== 0 arm below -- the failure mode is the old behaviour, not a dead
     page. Under reduced motion every duration (incl. SHIMMER_SWEEP) is
     zeroed, so this flips almost immediately with no branch needed. */
  var _introRevealed = false;

  function syncHeader(idx) {
    if (!headerGroup) return;
    var show = idx !== 0 || _introRevealed;
    if (_headerShown === show) return;
    _headerShown = show;

    gsap.to(headerGroup, {
      autoAlpha: show ? 1 : 0,
      duration: HEADER_FADE,
      ease: 'power2.out'
    });

    /* Scrim on its OWN tween, deliberately -- both start together, but the
       glass takes SCRIM_FADE (0.8s) against the nav's 0.3s, since matching
       the nav's duration made it read as a panel switching on rather than
       haze gathering. power1.out (not the nav's power2.out) keeps the long
       fade moving through its tail instead of arriving early and sitting.
       If these ever need to move as a unit again, recombine rather than
       hand-tuning two durations to look equal -- the split is chosen, not
       drift. */
    if (navScrim) {
      gsap.to(navScrim, {
        autoAlpha: show ? 1 : 0,
        duration: SCRIM_FADE,
        ease: 'power1.out'
      });
    }

    /* Set immediately rather than tweened: a half-faded nav shouldn't be
       clickable on the way out, and should be clickable as soon as it
       starts appearing. */
    if (headerCard) headerCard.style.pointerEvents = show ? 'auto' : 'none';
  }

  /* ── goTo ── */
  function goTo(idx) {
    if (idx < 0 || idx >= slides.length) return;

    var prev = current;

    syncHeader(idx);

    /* Cleared before dispatching to ANY path -- the morph/crossfade branches
       below hold `locked` for their whole run while `current` still names the
       OUTGOING slide (long finished), so leaving this armed would let a
       gesture mid-transition call skipEntrance() and start a second one on
       top. Each path re-arms it when it actually starts an entrance. */
    _entranceSkippable = false;

    if (MORPH_ENABLED && !REDUCED_MOTION && isMorphPair(prev, idx)) {
      doMorphTransition(prev, idx);
      return;
    }
    if (!REDUCED_MOTION && isBoxMorphForward(prev, idx)) {
      doMorphTransition(prev, idx);
      return;
    }
    if (!REDUCED_MOTION && isCrossfadePair(prev, idx)) {
      doCrossfade(prev, idx);
      return;
    }

    /* Only kill the OUTGOING slide. At init prev === idx === 0, so this
       would otherwise wipe the very slide about to animate — and that is a
       real bug, not a cosmetic one:

       a fromTo applies its from-state at BUILD time (immediateRender), but
       killSlide's clearProps strips it, and the subsequent tl.restart(true)
       renders the timeline at t=0 where a tween positioned LATER (the card
       is at HERO_CARD_START) has not started yet — so GSAP never re-applies
       it. Net effect: the card renders fully visible from page load, sits
       there, then snaps to hidden when the playhead finally reaches its
       tween. Verified in the browser; immediateRender:true and a tl.set()
       at position 0 both FAIL to fix it, because the problem is the wipe
       happening after build, not the from-state being absent.

       This is also what broke the per-element card sequence during the
       bisect: that commit was the first to give the card tween a non-zero
       position. Everything before it sat at position 0, where the tween IS
       active at t=0 and therefore gets re-rendered after the wipe — which
       is exactly why the baseline always looked fine. */
    if (prev !== idx) killSlide(slides[prev]);
    if (isScrollSlide(prev)) osExit();
    slides[prev].classList.remove('ls-active');

    current = idx;
    slides[current].classList.add('ls-active');
    if (isScrollSlide(idx)) osEnter();

    /* Same rebuild as enterViaEntranceTimeline does, for the same reason -- this
       is the OTHER path into a slide and both need it. */
    if (idx === 0 && _heroEntrancePlayed) timelines[0] = buildHeroReturnTl();

    var tl = timelines[idx];
    if (tl) {
      cleanAnimClasses(slides[current]);
      /* Re-arm before restart: every slide's entrance is skippable, and
         returning to one replays it, so it has to be skippable again or the
         second visit strands the same way the first one could.

         WAS (idx === 0). Slide 0 was the only skippable entrance, so on slides
         1-4 a gesture arriving during the entrance found nothing to skip and
         was DROPPED -- 0.92s of dead input entering the product card, 0.77s
         entering reviews, 0.45s entering the newsletter, with no escape. The
         slide-0 scoping came from the load-contention freeze, but that was
         about ADVANCING while the page still had ~166 requests in flight;
         slides 1-4 never enter during initial load, and skipping is cheap
         everywhere. */
      _entranceSkippable = true;
      tl.restart(true);
      scheduleUnlock((tl.duration() + ADVANCE_COOLDOWN) * 1000);
    } else {
      _entranceSkippable = false;
      scheduleUnlock(ADVANCE_COOLDOWN * 1000);
    }
  }

  /* ── Entrance skip ───────────────────────────────────────────────────────
     The first-load entrance runs several seconds and input is locked for its
     whole length (init lock() below, released by the scheduleUnlock that goTo
     arms off tl.duration()). Every gesture in that window used to be dropped
     on the floor by advance's `if (locked) return`, which on a phone is
     indistinguishable from a page that cannot scroll.

     A gesture during the entrance now ENDS the entrance instead of being
     ignored. The gesture is consumed by the skip and does not also advance —
     one gesture, one effect; the next one advances normally.

     Deliberately scoped to slide 0's timeline and nothing else. skipEntrance
     is a no-op during ordinary slide transitions (current !== 0, or the
     timeline already finished), so the lock still does its real job there:
     absorbing momentum tails so one hard flick can't skate through several
     slides. This is not "unlock on any input", it is "the long one is
     skippable".

     Returning to slide 0 replays the entrance (goTo restarts the timeline),
     and it is skippable then too, by the same test.

     progress(1, FALSE) — the second argument is suppressEvents and it is
     passed explicitly on purpose. The entrance's onComplete is what reveals
     the nav (_introRevealed, see buildHeroTl), so it has to fire; jumping the
     playhead with events suppressed would leave slide 0 permanently without a
     nav. Not relying on the default, which is easy to get wrong when reading
     this back. Worst case if a GSAP version ever did suppress it anyway, the
     failure mode is the already-documented one — no nav until you advance —
     and not a stranded page.

     The unlock is unconditional rather than a re-scheduled one: the timer
     goTo armed is for the FULL entrance length, so leaving it pending would
     re-lock nothing but would sit there for seconds ready to fire unlock()
     again. Clearing it keeps a single owner of the locked flag. ── */
  /* A FLAG, not tl.progress(). The progress test had a hole: between the
     timeline reporting 1 and the scheduleUnlock(duration + cooldown) timer
     firing, the page is still locked but entranceRunning() is already false --
     so a gesture in that window was dropped AND could not skip, and the
     visitor sat waiting out an animation that had already ended. Any stall
     that leaves progress at 1 early has the same effect for longer.

     The flag cannot land in that state: it is true from the moment an entrance
     timeline starts until something ends the entrance, and the only two things
     that end it are completing and being skipped.

     It still guards against skating: one skip per entrance, so the rest of a
     hard flick's momentum finds it false and is absorbed by the lock exactly as
     before.

     CLEARED AT THE TOP OF EVERY TRANSITION -- see goTo. That is what keeps a
     gesture mid-morph or mid-crossfade from satisfying this test and calling
     unlock(), which would let a second transition start on top of one already
     running. Both of those paths hold `locked` while `current` still names the
     OUTGOING slide, whose timeline finished long ago, so the flag is the only
     thing standing between them and a double transition. buildHeroTl's
     onComplete clears it too; that one is now belt-and-braces rather than the
     sole guard. Declared with the shared state above. */

  function entranceRunning() {
    return _entranceSkippable && !!timelines[current];
  }

  function skipEntrance() {
    if (!entranceRunning()) {
      return false;
    }
    _entranceSkippable = false;
    /* progress(1, FALSE) -- suppressEvents stays explicit; slide 0's entrance
       onComplete is what reveals the nav. Guarded because the flag no longer
       proves the timeline exists.

       timelines[current], not timelines[0]: this skips whichever entrance is
       actually running. entranceRunning() above has already established that
       there is one. */
    if (timelines[current]) timelines[current].progress(1, false);
    if (_unlockTimer) {
      clearTimeout(_unlockTimer);
      _unlockTimer = null;
    }
    unlock();
    return true;
  }

  /* ── advance ── */
  function advance(dir) {
    /* Locked: if that's the entrance, this gesture ENDS it and is consumed.
       One gesture, one effect. The card lands in full and you stay on slide 0;
       the next gesture advances normally.

       REVERTED 2026-08-27 from "skip and advance in the same movement"
       (9cbf33a, 2026-08-26). Carrying through looked better in isolation but
       was measurably worse on a phone: the entrance runs while the page is
       still loading, so a touch in that window kicked off a full slide
       transition -- building the next slide, running its timeline, the morph --
       on top of a load that already had 166 requests in flight. Reported as
       "if you touch it and try to scroll it freezes and takes MUCH longer",
       against a page that scrolled fine if left alone. Ending the animation is
       cheap; advancing during load is not.

       Any other lock -- a transition in flight -- still swallows the input
       exactly as before, because skipEntrance only ever acts on the entrance.

       The touchend tap-to-skip handler matches this: a tap is not a scroll
       gesture and must not move the page. Both now mean "stop waiting". */
    /* ONE GESTURE, ONE ADVANCE. Observer reports raw direction of travel and
       keeps firing onUp/onDown for as long as a drag continues past
       `tolerance`, so a single swipe reaches here 9-12 times (measured on a
       real iPhone: 98 advance() calls against 11 touchends).

       Until now the LOCK absorbed the surplus -- the first call locked and the
       rest fell into the locked branch. That is not a guard, it is a side
       effect, and it stops holding the moment anything unlocks mid-gesture.
       skipEntrance() does exactly that, and now does it on every slide rather
       than only the intro, so calls 2-9 of one swipe would arrive UNLOCKED and
       be free to advance. One swipe, several slides.

       Trailing edge on purpose: a suppressed call still refreshes the stamp,
       so a long drag stays one advance however many events it emits, and the
       next deliberate gesture needs GESTURE_QUIET_MS of quiet first. Same
       intent as WHEEL_QUIET_MS on the unlock side -- momentum tails must not
       count as a second gesture.

       Placed above the locked branch so the FIRST call of a gesture still
       reaches skipEntrance and the rest cannot. */
    var _nowMs = Date.now();
    /* Two conditions, and the second is the trackpad fix. The window keeps
       extending while input keeps arriving -- correct for a swipe, whose tail
       is short -- but it can never hold input for more than
       GESTURE_HOLD_MAX_MS past the last advance that was actually honoured.

       Inertial scrolling is the case that breaks a pure trailing edge: the
       events outlive the gesture by seconds, so "quiet" never arrives and the
       page stops responding. Bounded, a long inertia run advances at most once
       per second instead of nine times per flick, which is navigable rather
       than either broken state. */
    if (_nowMs - _lastGestureAt < GESTURE_QUIET_MS &&
        _nowMs - _lastAdvanceAt  < GESTURE_HOLD_MAX_MS) {
      _lastGestureAt = _nowMs;
      return;
    }
    _lastGestureAt = _nowMs;
    _lastAdvanceAt = _nowMs;

    if (locked) {
      skipEntrance();
      return;
    }
    var next = current + dir;
    if (next < 0 || next >= slides.length) {
      return;
    }
    lock();
    goTo(next);
  }

  /* ── Combined wheel handler ── */
  container.addEventListener('wheel', function (e) {
    _lastWheelTime = Date.now();

    /* Customer Reviews: an expanded, overflowing review card owns wheel
       input entirely while it's open -- never advances the slide, even at
       its own scroll boundary (matches "expanding must not trigger a slide
       advance," not just "while there's room left to scroll"). Native
       scroll is allowed to happen (no preventDefault) so the browser
       handles it normally. */

    /* Non-OS slides: block native scroll entirely */
    if (!isScrollSlide(current)) {
      e.preventDefault();
      return;
    }

    /* Our Story: entrance still playing — absorb all input */
    if (!osScrollEnabled()) {
      e.preventDefault();
      return;
    }

    /* Our Story: boundary detection */
    if (e.deltaY > 0) {
      if (osAtBottom()) {
        e.preventDefault();
        /* One-shot: start timer only on first bottom contact. Never reset.
           Advance is triggered by Observer onDown once _osBottomSettled is true. */
        if (!_osBottomSettleTimer && !_osBottomSettled) {
          _osBottomSettleTimer = setTimeout(function () {
            _osBottomSettleTimer = null;
            if (isScrollSlide(current) && osAtBottom()) {
              _osBottomSettled = true;
            }
          }, SETTLE_QUIET_MS);
        }
      } else {
        /* Scrolling down through content — away from top; reset top settle */
        clearTimeout(_osTopSettleTimer);
        _osTopSettleTimer = null;
        _osTopSettled = false;
      }
    } else if (e.deltaY < 0) {
      if (osAtTop()) {
        e.preventDefault();
        /* One-shot: start timer only on first top contact (or return to top).
           Advance is triggered by Observer onUp once _osTopSettled is true. */
        if (!_osTopSettleTimer && !_osTopSettled) {
          _osTopSettleTimer = setTimeout(function () {
            _osTopSettleTimer = null;
            if (isScrollSlide(current) && osAtTop()) {
              _osTopSettled = true;
            }
          }, SETTLE_QUIET_MS);
        }
      } else {
        /* Scrolling up through content — away from bottom; reset bottom settle */
        clearTimeout(_osBottomSettleTimer);
        _osBottomSettleTimer = null;
        _osBottomSettled = false;
      }
    }

  }, { passive: false });

  /* ── Touch: block native scroll on non-OS slides ── */
  container.addEventListener('touchmove', function (e) {
    if (!isScrollSlide(current)) e.preventDefault();
  }, { passive: false });

  container.addEventListener('touchstart', function (e) {
    if (!isScrollSlide(current)) return;
    _osTouchY0 = e.touches[0].clientY;
    if (!osScrollEnabled()) e.preventDefault();
  }, { passive: false });

  container.addEventListener('touchend', function (e) {
    if (!isScrollSlide(current) || _osTouchY0 === null) return;
    if (!osScrollEnabled()) { _osTouchY0 = null; return; }
    var dy = _osTouchY0 - e.changedTouches[0].clientY;
    _osTouchY0 = null;
    if (Math.abs(dy) < 40) return;
    setTimeout(function () {
      if (!isScrollSlide(current)) return;
      if (dy > 0 && osAtBottom()) advance(1);
      else if (dy < 0 && osAtTop())  advance(-1);
    }, 150);
  }, { passive: true });

  /* ── Touch: tap-to-skip the entrance ──
     A SWIPE during the entrance already reaches skipEntrance through the
     Observer -> advance path below. A TAP does not: it never travels past
     Observer's `tolerance: 4`, so no onDown/onUp ever fires and the gesture
     would otherwise vanish. This is the tap half of "a touch during the
     entrance ends it".

     Fires on touchend rather than touchstart so it can't fight the browser's
     own gesture disambiguation, and there is no need to tell a tap from a
     swipe here — either one means "stop waiting", and skipEntrance is
     idempotent, so the swipe path arriving first simply makes this a no-op.

     passive: true — this only reads. Native scroll on non-OS slides is
     already blocked by the touchmove handler above, and nothing here should
     add a second reason for the browser to wait on a listener before
     scrolling. ── */
  container.addEventListener('touchend', function () {
    skipEntrance();
  }, { passive: true });

  /* ── Gesture -> direction ──────────────────────────────────────────────────
     goForward is "move to the next slide", goBack is "move to the previous
     one". Which physical gesture means which is NOT the same for wheel and
     touch, which is why these are named by INTENT and wired to opposite
     Observer callbacks below. Do not collapse the two Observers back into one.

     Observer reports the RAW direction of travel, it does not normalise the
     two input types against each other:
       wheel  — deltaY > 0 is "scrolling down", which means go FORWARD.
                Observer calls onDown. Forward == onDown.
       touch  — onDown fires when the FINGER moves down, which is the gesture
                for pulling the page down to see what is above, i.e. go BACK.
                Forward == onUp. The opposite mapping.

     Both types used to share one Observer with onDown -> forward, so the
     wheel was right and touch was exactly backwards: on the intro card a
     downward swipe advanced, and an upward swipe (the natural "next")
     did nothing, because advance(-1) off slide 0 is a no-op.

     It also silently broke Our Story's internal scroll, which is how it was
     found. An upward swipe there fired onUp, which hit the _osTopSettled
     latch that finishMorph arms on arrival, and reversed to the intro card
     before the browser could scroll the panel. The slide looked unscrollable
     when in fact every gesture was being spent going backwards.

     The touchend handler further up already uses the correct convention
     (dy > 0, finger up, means forward), so the file contradicted itself. This
     is the half that was wrong. ── */
  function goForward() {
    if (isScrollSlide(current)) {
      /* At Our Story, forward only fires once the reader has settled at the
         BOTTOM of the panel. Until then this is deliberately a no-op so the
         gesture falls through to the browser and scrolls the panel. */
      if (_osBottomSettled) {
        _osBottomSettled = false;
        advance(1);
      }
      return;
    }
    advance(1);
  }

  function goBack() {
    if (isScrollSlide(current)) {
      if (_osTopSettled) {
        _osTopSettled = false;
        advance(-1);
      }
      return;
    }
    advance(-1);
  }

  /* Wheel: scrolling down (onDown) goes forward. */
  Observer.create({
    target: container,
    type: 'wheel',
    tolerance: 4,
    preventDefault: false,
    onDown: goForward,
    onUp: goBack
  });

  /* Touch: SWAPPED relative to the wheel above, deliberately. Dragging the
     finger UP is the "next" gesture, and Observer reports that as onUp. */
  Observer.create({
    target: container,
    type: 'touch',
    tolerance: 4,
    preventDefault: false,
    onDown: goBack,
    onUp: goForward
  });

  /* ── Keyboard ──────────────────────────────────────────────────────────
     ON A SCROLL SLIDE, ARROWS SCROLL THE PANEL AND ONLY ADVANCE AT THE EDGE --
     the same contract the pointer has always had.

     Before this, every key called advance() directly AND preventDefault()'d, so
     a keyboard user could not scroll Our Story at all: one press jumped the whole
     slide. They never reached the photograph or the lower half of the copy. That
     is an accessibility bug in its own right, not just an inconsistency.

     FOLLOWS THE TOUCH MODEL, NOT THE WHEEL ONE, and that distinction is
     load-bearing. The wheel path advances via goForward() gated on
     _osBottomSettled, a latch that exists to stop momentum carrying the reader
     past the photo -- and that latch is armed ONLY inside the wheel handler.
     Routing keys through goForward() would mean it never arms for someone who
     never touches a wheel, and a keyboard-only visitor could never leave Our
     Story at all. touchend already checks osAtBottom() directly for the same
     reason: a finger lift is discrete, and so is a keypress. Neither needs
     protecting from momentum it cannot generate.

     The scroll is programmatic rather than native. .page-content is a div with
     overflow-y:auto, and arrows only scroll it natively when it holds focus --
     with focus on <body>, the browser tries to scroll the document, which on this
     page does not scroll. Leaving it to the browser would look like the key doing
     nothing.

     Reduced motion is irrelevant here: this is a scroll position change, not an
     animation, and native arrow scrolling is instant regardless. ── */
  var KEY_SCROLL_STEP = 64;    /* px, per arrow press — near the browser default */
  var KEY_PAGE_FRACTION = 0.9; /* PageUp/Down/Space, as a fraction of the viewport */

  document.addEventListener('keydown', function (e) {
    var tag = (document.activeElement || {}).tagName || '';
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;

    var dir = 0, paged = false;
    if (e.key === 'ArrowDown')      { dir = 1; }
    else if (e.key === 'PageDown')  { dir = 1; paged = true; }
    else if (e.key === 'ArrowUp')   { dir = -1; }
    else if (e.key === 'PageUp')    { dir = -1; paged = true; }
    else if (e.key === ' ')         { dir = e.shiftKey ? -1 : 1; paged = true; }
    else return;

    e.preventDefault();

    /* osScrollEnabled(), not isScrollSlide() alone: the class also means "the
       entrance has finished", so during it a key advances exactly as it did
       before rather than scrolling a panel that is still animating in. */
    if (isScrollSlide(current) && osScrollEnabled()) {
      var atEdge = dir > 0 ? osAtBottom() : osAtTop();
      if (!atEdge) {
        var step = paged
          ? Math.round(container.clientHeight * KEY_PAGE_FRACTION)
          : KEY_SCROLL_STEP;
        container.scrollTop += dir * step;
        return;
      }
    }

    advance(dir);
  });

  /* ── First-load video reveal ────────────────────────────────────────────
     The eggshell curtain (.ie-video-bg__reveal) is already fully opaque from
     first paint via CSS — see sections/ie-video-bg.liquid, gated on
     html.js-slider-ready which the inline <head> script sets before anything
     renders. This only fades it off.

     NOT gated on any video event. The <video> carries a poster of its own
     first frame, so the water is painted with the page and there is nothing
     to wait for. An earlier version waited on loadeddata, which meant the
     curtain sat opaque until that fired (or a timeout did) — visibly a
     hold-then-go rather than a reveal.

     Deliberately independent of the hero timeline: this must never be able
     to delay or block the card's own entrance, and nothing here touches
     goTo() or timelines[].

     First load only — the wrapper is position:fixed and stays behind every
     slide, so no morph or crossfade path touches this. autoAlpha, not
     display:none: it animates opacity AND flips visibility:hidden at 0, so
     the curtain stops rendering and can't intercept anything once done
     (pointer-events:none is on it permanently from CSS regardless). ── */
  function runFirstLoadReveal() {
    var overlay = document.querySelector('.ie-video-bg__reveal');
    if (!overlay) return;

    /* power1.out — the WEAKEST out ease, chosen deliberately.

       Settled after sampling three candidates at 2.2s:
         power1.inOut  50% 1.10s | 2% 1.98s  — eased in, so the curtain sat
                       near full opacity through the first third. Read as a
                       hold before anything happened.
         power3.out    50% 0.35s | 2% 1.37s  — cleared far too fast; the last
                       38% of the reveal was under 2% opacity, i.e. nothing.
         power1.out    50% 0.63s | 2% 1.89s  — decelerates into transparency
                       while staying readable deep into the reveal.

       Counterintuitive bit worth keeping: on a 1 -> 0 tween a STRONGER out
       ease clears FASTER, not slower. power3's tail is longer mathematically
       but not perceptually. Weaker = more visible curtain for longer. */
    gsap.to(overlay, {
      autoAlpha: 0,
      duration: VIDEO_REVEAL_DURATION,
      ease: 'power1.out'
    });

    /* NO scale tween here — removed deliberately, don't reinstate as-is.
       A 1.06 -> 1.00 scale on the video shimmered badly: a playing video has
       no cached texture, so every decoded frame is rasterised fresh at the
       current scale factor and lands on a different resampling grid. On
       water, which is all high-frequency detail, that's shimmer by
       construction. Layer promotion (will-change) does not help for the same
       reason — there is nothing to cache.

       Hiding it behind the curtain was considered and rejected: at 1.2s the
       curtain is only ~21% opaque (power1.out is (1-p)^2), so the tail stays
       visible, and finishing early enough to truly hide it leaves the video
       static for the whole second half — exactly when it's finally visible.
       That keeps every cost and loses the effect.

       FOLLOW-UP if depth is wanted: a FIXED scale(1.06) plus a slow animated
       translate within that headroom. A constant scale rasterises once, and
       translation is pure compositing, so neither resamples per frame — same
       drift, no shimmer, and the overscale guarantees no exposed edges. */
  }

  /* ── Init: lock until first entrance completes ── */
  lock();
  runFirstLoadReveal();
  goTo(0);

  /* If the page already gave up waiting for us, do NOT play the entrance.
     layout/theme.liquid's inline head script drops .js-slider-ready after 3s
     when __lsAlive is still unset, which switches off ie-intro's hidden states
     and leaves a static, fully visible card. Arriving after that and animating
     anyway would hide the card again and re-reveal it -- a double reveal, which
     reads worse than either losing the entrance or waiting for it.

     Synchronous and immediately after goTo(0), so the from-state that
     tl.restart(true) just rendered is overwritten in the same frame and never
     paints. skipEntrance also clears the lock, so input works at once. */
  if (!document.documentElement.classList.contains('js-slider-ready')) {
    skipEntrance();
  }

  /* THIS MUST BE THE LAST STATEMENT IN THE FILE -- it asserts "init
     completed", not "the file started". layout/theme.liquid's inline <head>
     script reads it at DOMContentLoaded to decide whether to strip
     .js-slider-ready and fail open (static visible card, no nav wait).
     Moving this earlier previously let a throw mid-init leave it TRUE with
     recovery never running. Safe to be last since init is fully synchronous
     and this file is deferred. Full reasoning: CLAUDE.md, "Landing slider". */
  window.__lsAlive = true;

})();
